import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import * as XLSX from "xlsx";
import { resolveFileContentType } from "../utils/s3Upload";

const API_URL = process.env.REACT_APP_API_URL;
const MAX_SUPPLIER_ATTACHMENTS = 5;
const SUPPLIER_ALLOWED_EXTENSIONS = ["jpeg", "jpg", "png", "pdf", "xlsx", "ppt"];
/** Paginate Materials / BOQ lanes when count exceeds this size. */
const MATERIALS_PAGE_SIZE = 100;
const BOQ_LANES_PAGE_SIZE = 100;

/** Window of page number buttons: Prev 1 2 3 4 5 Next */
function getVisiblePageNumbers(currentPage, totalPages, windowSize = 5) {
  if (totalPages <= 0) return [];
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  let start = Math.max(0, currentPage - Math.floor(windowSize / 2));
  let end = start + windowSize;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(0, end - windowSize);
  }
  return Array.from({ length: end - start }, (_, i) => start + i);
}

function TablePaginationBar({
  page,
  pageSize,
  totalItems,
  onPageChange,
  itemLabel = "items",
}) {
  if (totalItems <= pageSize) return null;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);
  const from = safePage * pageSize + 1;
  const to = Math.min((safePage + 1) * pageSize, totalItems);
  const pageNumbers = getVisiblePageNumbers(safePage, totalPages, 5);

  return (
    <div
      className="d-flex align-items-center flex-wrap mb-2 mt-1"
      style={{ gap: "4px" }}
    >
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={safePage <= 0}
        onClick={() => onPageChange(safePage - 1)}
      >
        Prev
      </button>
      {pageNumbers.map((pageIndex) => (
        <button
          key={`page-${pageIndex}`}
          type="button"
          className={`btn btn-sm ${
            pageIndex === safePage ? "btn-primary" : "btn-outline-secondary"
          }`}
          onClick={() => onPageChange(pageIndex)}
          style={{ minWidth: "32px" }}
        >
          {pageIndex + 1}
        </button>
      ))}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={safePage >= totalPages - 1}
        onClick={() => onPageChange(safePage + 1)}
      >
        Next
      </button>
      <span className="text-muted ml-2" style={{ fontSize: "12px" }}>
        {from}–{to} of {totalItems} {itemLabel}
      </span>
    </div>
  );
}

// ─── Identical utility functions from Rfq-Supplier-view ──────────────────────

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

/** Resolve base64 xlsx string from export API. */
function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return t;
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    const comma = t.indexOf(",");
    if (t.includes("base64") && comma > 0) {
      return extractExcelBase64(t.slice(comma + 1));
    }
    return null;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (typeof raw.data === "string") return extractExcelBase64(raw.data);
    if (raw.data != null && typeof raw.data === "object") {
      const nested = extractExcelBase64(raw.data);
      if (nested) return nested;
    }
    if (raw.file != null) return extractExcelBase64(raw.file);
    if (raw.fileBase64 != null) return extractExcelBase64(raw.fileBase64);
    if (raw.base64 != null) return extractExcelBase64(raw.base64);
    if (raw.excel != null) return extractExcelBase64(raw.excel);
  }
  return null;
}

function downloadXlsxFromBase64(base64, fileName) {
  const clean = String(base64 || "").trim();
  if (!clean.startsWith("UEs")) {
    throw new Error("Invalid Excel data received");
  }
  const byteCharacters = atob(clean);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || `lrfq-supplier-quote-${Date.now()}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function normalizeExcelHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[%#]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

function normalizeExcelKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function excelCellString(value) {
  if (value == null) return "";
  return String(value).trim();
}

function isBlankExcelValue(value) {
  const v = excelCellString(value);
  if (!v) return true;
  const lower = v.toLowerCase();
  return (
    v === "-" ||
    v === "--" ||
    v === "—" ||
    v === "–" ||
    lower === "n/a" ||
    lower === "na" ||
    lower === "null" ||
    lower === "undefined"
  );
}

function pickExcelField(row, aliases) {
  if (!row || typeof row !== "object") return "";
  const wanted = aliases.map(normalizeExcelHeader);
  for (const [key, value] of Object.entries(row)) {
    if (wanted.includes(normalizeExcelHeader(key))) {
      const text = excelCellString(value);
      return isBlankExcelValue(text) ? "" : text;
    }
  }
  return "";
}

const EXCEL_FIELD_ALIASES = {
  lineKey: ["linekey", "line", "lineno", "sno", "sno", "key", "rowkey"],
  materialCode: [
    "materialcode",
    "code",
    "itemcode",
    "sourcecode",
    "cluster",
    "clustercode",
  ],
  basicPrice: [
    "basicprice",
    "price",
    "unitprice",
    "quotedprice",
    "basic",
    "rate",
  ],
  technicalResponse: [
    "technicalresponse",
    "technicalremarks",
    "comments",
    "response",
    "techresponse",
  ],
  boqNo: ["boqno", "boqid", "boq", "boqline"],
  rfqTerm: ["rfqterm", "lrfqterm", "term", "terms", "termname"],
  quoteResponse: [
    "quoteresponse",
    "termscomments",
    "supplierresponse",
    "termsresponse",
    "comments",
    "response",
  ],
};

function buildExcelLineTargetMap(materialsList) {
  const map = new Map();
  const setKey = (key, target) => {
    const normalized = normalizeExcelKey(key);
    if (!normalized || map.has(normalized)) return;
    map.set(normalized, target);
  };

  (Array.isArray(materialsList) ? materialsList : []).forEach(
    (material, index) => {
      const lineNo =
        material?.lineNo != null && material.lineNo !== ""
          ? String(material.lineNo)
          : String(index + 1);
      setKey(lineNo, { type: "material", index });
      setKey(`M${index}`, { type: "material", index });
      setKey(`L${lineNo}`, { type: "material", index });

      const code = String(
        material?.sourceCode || material?.materialCode || "",
      ).trim();
      if (code && code !== "-") {
        setKey(`CODE:${code}`, { type: "material", index });
      }

      const boqItems = getMaterialBoqItems(material);
      boqItems.forEach((boqItem, boqIndex) => {
        const boqNo = String(
          boqItem?.boqNo || boqItem?.sNo || `${lineNo}.${boqIndex + 1}`,
        ).trim();
        setKey(boqNo, { type: "boq", index, boqIndex });
        setKey(`${lineNo}.${boqIndex + 1}`, { type: "boq", index, boqIndex });
        setKey(`M${index}-B${boqIndex}`, { type: "boq", index, boqIndex });
      });
    },
  );

  return map;
}

function resolveExcelRowTarget(row, lineMap, materialsList, rowIndex) {
  const lineKey = pickExcelField(row, EXCEL_FIELD_ALIASES.lineKey);
  if (lineKey) {
    const byKey = lineMap.get(normalizeExcelKey(lineKey));
    if (byKey) return byKey;
  }
  const boqNo = pickExcelField(row, EXCEL_FIELD_ALIASES.boqNo);
  if (boqNo) {
    const byBoq = lineMap.get(normalizeExcelKey(boqNo));
    if (byBoq) return byBoq;
  }
  const code = pickExcelField(row, EXCEL_FIELD_ALIASES.materialCode);
  if (code && code !== "-") {
    const byCode = lineMap.get(normalizeExcelKey(`CODE:${code}`));
    if (byCode) return byCode;
  }
  const flat = [];
  (Array.isArray(materialsList) ? materialsList : []).forEach(
    (material, index) => {
      flat.push({ type: "material", index });
      getMaterialBoqItems(material).forEach((_, boqIndex) => {
        flat.push({ type: "boq", index, boqIndex });
      });
    },
  );
  return flat[rowIndex] || null;
}

function workbookSheetsToRowGroups(workbook) {
  const sheetNames = workbook.SheetNames || [];
  const groups = { materials: [], terms: [] };

  sheetNames.forEach((name) => {
    const sheet = workbook.Sheets[name];
    if (!sheet) return;
    const rows = XLSX.utils.sheet_to_json(sheet, {
      defval: "",
      raw: false,
      blankrows: false,
    });
    if (!Array.isArray(rows) || rows.length === 0) return;

    const sheetKey = normalizeExcelHeader(name);
    const sample = rows[0] || {};
    const headers = Object.keys(sample).map(normalizeExcelHeader);
    const looksLikeTerms =
      sheetKey.includes("term") ||
      headers.some((h) =>
        ["rfqterm", "lrfqterm", "term", "terms", "quoteresponse"].includes(h),
      );
    const looksLikeMaterials =
      sheetKey.includes("material") ||
      sheetKey.includes("quote") ||
      sheetKey.includes("boq") ||
      headers.some((h) =>
        ["linekey", "materialcode", "sourcecode", "basicprice", "cluster"].includes(
          h,
        ),
      );

    if (looksLikeTerms && !looksLikeMaterials) {
      groups.terms.push(...rows);
    } else if (looksLikeMaterials || groups.materials.length === 0) {
      groups.materials.push(...rows);
    } else if (looksLikeTerms) {
      groups.terms.push(...rows);
    }
  });

  if (groups.materials.length === 0 && sheetNames.length > 0) {
    const first = XLSX.utils.sheet_to_json(workbook.Sheets[sheetNames[0]], {
      defval: "",
      raw: false,
      blankrows: false,
    });
    groups.materials = Array.isArray(first) ? first : [];
  }

  return groups;
}

async function parseSupplierQuoteExcelFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  return workbookSheetsToRowGroups(workbook);
}

// LRFQ response uses lrfqNumber / lrfqId instead of rfqNumber
function pickLrfqFromResponse(responseData) {
  let dataBlock =
    responseData?.data != null ? responseData.data : responseData;
  if (dataBlock == null) return null;
  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") return null;

  if (
    !Array.isArray(dataBlock) &&
    (dataBlock.lrfqNumber != null ||
      dataBlock.lrfqId != null ||
      dataBlock.materials != null ||
      dataBlock.terms != null)
  ) {
    return dataBlock;
  }
  return null;
}

function formatDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, "/");
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")}`;
}

function formatDueDateTime(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}/.test(str)) {
    return str.replace("T", " ");
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, "/");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function parseRfqDueDate(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split("-");
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), 23, 59, 59, 999);
  }
  if (/^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}/.test(str)) {
    const [datePart, timePart] = str.split(/[ T]/);
    const [dd, mm, yyyy] = datePart.split("-");
    const [hh, min] = timePart.split(":");
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      59,
      999,
    );
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isRfqDueDatePassed(dueDate) {
  const due = parseRfqDueDate(dueDate);
  if (!due) return false;
  return Date.now() > due.getTime();
}

/** After main due date, only suppliers with an active targetPriceDueDate may update. */
function isTargetPriceDueDateActive(value) {
  const due = parseRfqDueDate(value);
  if (!due) return false;
  return Date.now() <= due.getTime();
}

function getSupplierPortalTargetPriceDueDate(lrfq) {
  if (!lrfq || typeof lrfq !== "object") return "";
  return String(
    lrfq.targetPriceDueDate ||
      lrfq.supplier?.targetPriceDueDate ||
      lrfq.priceMatchingDueDate ||
      "",
  ).trim();
}

/**
 * Before LRFQ due date → any supplier can quote/update.
 * After LRFQ due date → only with a valid (not expired) targetPriceDueDate.
 */
function canSupplierUpdateLrfqQuote(lrfq) {
  if (!isRfqDueDatePassed(lrfq?.lrfqDueDate)) return true;
  return isTargetPriceDueDateActive(getSupplierPortalTargetPriceDueDate(lrfq));
}

function isDecimalInput(value) {
  if (value === "" || value == null) return true;
  const normalized = String(value).trim().replace(/,/g, "");
  return /^\d*\.?\d*$/.test(normalized);
}

function normalizeDecimalInput(value) {
  if (value == null) return "";
  return String(value).trim().replace(/,/g, "").replace(/\s/g, "");
}

function parseDecimalValue(value) {
  const normalized = normalizeDecimalInput(value);
  if (normalized === "" || normalized === ".") return NaN;
  return parseFloat(normalized);
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function roundMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return NaN;
  return Math.round(num * 100) / 100;
}

/**
 * Latest target from targetPrice[] entry (prefer newest history, else entry.targetPrice).
 */
function getLatestTargetFromEntry(entry) {
  if (entry == null || typeof entry !== "object") return NaN;
  const history = Array.isArray(entry.history) ? entry.history : [];
  if (history.length > 0) {
    const sorted = [...history].sort(
      (a, b) =>
        new Date(b?.updatedAt || 0).getTime() -
        new Date(a?.updatedAt || 0).getTime(),
    );
    const fromHist = Number(sorted[0]?.targetPrice);
    if (Number.isFinite(fromHist) && fromHist > 0) return fromHist;
  }
  const direct = Number(entry.targetPrice);
  return Number.isFinite(direct) && direct > 0 ? direct : NaN;
}

function collectItemIds(item) {
  return [
    item?.uniqueId,
    item?._id,
    item?.boq_id,
    item?.boqId,
    item?.id,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);
}

/** uniqueId (and related ids) → latest targetPrice from lrfq.targetPrice array. */
function buildLrfqTargetMap(lrfq) {
  const map = {};
  const list = Array.isArray(lrfq?.targetPrice) ? lrfq.targetPrice : [];
  list.forEach((entry) => {
    const latest = getLatestTargetFromEntry(entry);
    if (!Number.isFinite(latest)) return;
    collectItemIds(entry).forEach((uid) => {
      map[uid] = latest;
    });
  });
  return map;
}

function getLineTargetPrice(item, targetMap = {}) {
  const fromItem = Number(item?.targetPrice);
  if (Number.isFinite(fromItem) && fromItem > 0) return fromItem;
  for (const uid of collectItemIds(item)) {
    const mapped = Number(targetMap[uid]);
    if (Number.isFinite(mapped) && mapped > 0) return mapped;
  }
  return NaN;
}

/** True when a positive target exists for the line. */
function hasLineTargetPrice(targetPrice) {
  const target = Number(targetPrice);
  return Number.isFinite(target) && target > 0;
}

function getPreviousQuoteMaxTotal(lrfq) {
  const fromGross = Number(lrfq?.grossTotal?.grossTotalValue);
  if (Number.isFinite(fromGross) && fromGross > 0) return fromGross;

  const quotes = Array.isArray(lrfq?.materialQuotes) ? lrfq.materialQuotes : [];
  if (quotes.length > 0) {
    const sum = quotes.reduce(
      (acc, q) => acc + (Number(q?.totalValue) || 0),
      0,
    );
    if (sum > 0) return sum;
  }

  const materials = Array.isArray(lrfq?.materials) ? lrfq.materials : [];
  const fromMaterials = materials.reduce(
    (acc, m) => acc + (Number(m?.totalValue) || 0),
    0,
  );
  return fromMaterials > 0 ? fromMaterials : NaN;
}

/**
 * Resolve the active price-matching round id for update-lrfq-supplier-quote.
 * Prefer root fields from get-supplier-lrfq-detail; else latest targetPrice history id.
 */
function resolveLrfqTargetPriceId(lrfq) {
  if (!lrfq || typeof lrfq !== "object") return "";

  const direct = String(
    lrfq.targetPriceId ||
      lrfq.latestTargetPriceId ||
      lrfq.priceMatchingTargetId ||
      lrfq.priceMatchingId ||
      lrfq.priceMatching?._id ||
      lrfq.priceMatching?.id ||
      lrfq.priceMatching?.targetPriceId ||
      "",
  ).trim();
  if (direct) return direct;

  const list = Array.isArray(lrfq.targetPrice) ? lrfq.targetPrice : [];
  let latestId = "";
  let latestTs = -1;
  list.forEach((entry) => {
    const history = Array.isArray(entry?.history) ? entry.history : [];
    history.forEach((row) => {
      const id = String(row?.uniqueId || row?._id || row?.id || "").trim();
      if (!id) return;
      const ts = new Date(row?.updatedAt || 0).getTime();
      const safeTs = Number.isFinite(ts) ? ts : 0;
      if (safeTs >= latestTs) {
        latestTs = safeTs;
        latestId = id;
      }
    });
  });
  return latestId;
}

function isQuoteSubmitted(lrfq) {
  if (!lrfq) return false;
  if (lrfq.quoteSubmitted === true || lrfq.quoteSubmitted === "true")
    return true;
  if (lrfq.quoteSubmittedAt) return true;
  const status = String(lrfq.quoteStatus || "").trim().toLowerCase();
  return status === "submitted";
}

function normalizeQuoteResponse(value) {
  return String(value ?? "").trim();
}

function renderCenteredDash(value) {
  const isEmpty =
    value === null ||
    value === undefined ||
    String(value).trim() === "";
  return isEmpty ? (
    <span className="d-block text-center">-</span>
  ) : (
    value
  );
}

function toEditableNumberInput(value) {
  if (value == null || value === "") return "";
  const str = String(value).trim();
  if (str === "" || str === "-" || str === ".") return "";
  const num = Number(str.replace(/,/g, ""));
  if (!Number.isFinite(num) || num === 0) return "";
  return str;
}

// ─── LRFQ-specific: per-material price state ─────────────────────────────────

function getMaterialBoqItems(material) {
  if (Array.isArray(material?.boqItems) && material.boqItems.length > 0) {
    return material.boqItems;
  }
  if (material?.isBoq && Array.isArray(material?.boq) && material.boq.length > 0) {
    return material.boq;
  }
  return [];
}

function hasLrfqTargetPrice(lrfq, targetMap = {}) {
  if (Object.values(targetMap).some((v) => Number(v) > 0)) return true;
  const materials = Array.isArray(lrfq?.materials) ? lrfq.materials : [];
  for (const material of materials) {
    if (Number.isFinite(getLineTargetPrice(material, targetMap))) return true;
    for (const boqItem of getMaterialBoqItems(material)) {
      if (Number.isFinite(getLineTargetPrice(boqItem, targetMap))) return true;
    }
  }
  return false;
}

/** uniqueId → previously submitted basicPrice (cap for price matching updates). */
function buildPreviousQuotePriceMap(lrfq) {
  const map = {};
  const remember = (uid, price) => {
    const id = String(uid || "").trim();
    const num = Number(price);
    if (!id || !Number.isFinite(num) || num < 0) return;
    map[id] = num;
  };

  const quotes = Array.isArray(lrfq?.materialQuotes) ? lrfq.materialQuotes : [];
  if (quotes.length > 0) {
    quotes.forEach((quote) => {
      remember(quote?.uniqueId, quote?.basicPrice);
      const lanes = Array.isArray(quote?.boq)
        ? quote.boq
        : Array.isArray(quote?.boqItems)
          ? quote.boqItems
          : [];
      lanes.forEach((lane) => remember(lane?.uniqueId, lane?.basicPrice));
    });
    return map;
  }

  const materials = Array.isArray(lrfq?.materials) ? lrfq.materials : [];
  materials.forEach((material) => {
    remember(material?.uniqueId, material?.basicPrice);
    getMaterialBoqItems(material).forEach((lane) =>
      remember(lane?.uniqueId, lane?.basicPrice),
    );
  });
  return map;
}

function getBoqItemQuantity(row, boqIndex, boqItem) {
  const qty = parseDecimalValue(row?.boqPrices?.[boqIndex]?.quantity);
  if (Number.isFinite(qty) && qty > 0) return qty;

  const sourceQty = parseFloat(boqItem?.quantity);
  return Number.isFinite(sourceQty) && sourceQty > 0 ? sourceQty : 1;
}

// Percentage widths so the materials table fills the card after GST columns were removed.
// BOQ nested table spans Source Code → Basic Price so Qty / Basic Price stay in a straight column.
const MATERIAL_TABLE_COLUMN_WIDTHS = {
  category: {
    sNo: "3%",
    sourceCode: "9%",
    sourceDescription: "34%",
    hsn: "7%",
    uom: "5%",
    quantity: "6%",
    targetPrice: "8%",
    basicPrice: "14%",
    totalValue: "7%",
    technicalResponse: "9%",
  },
  boq: {
    // Lane columns share the space left of aligned Qty + Basic Price.
    indent: "0px",
    sNo: "8%",
    sourcePincode: "10%",
    destinationPincode: "10%",
    sourceCity: "9%",
    destinationCity: "9%",
    truckName: "10%",
    truckBody: "7%",
    loadability: "9%",
    lengthFt: "7%",
    widthFt: "7%",
    heightFt: "7%",
    // quantity / targetPrice / basicPrice are computed to match parent columns.
    quantity: "8%",
    targetPrice: "10%",
    basicPrice: "18%",
  },
};

/**
 * Nested BOQ widths: Qty / Basic Price (and Target) match parent columns;
 * remaining % is shared by lane columns so the table still sums to 100%.
 */
function getBoqColumnWidths(withTarget) {
  const c = MATERIAL_TABLE_COLUMN_WIDTHS.category;
  const nestPct =
    parseFloat(c.sourceCode) +
    parseFloat(c.sourceDescription) +
    parseFloat(c.hsn) +
    parseFloat(c.uom) +
    parseFloat(c.quantity) +
    parseFloat(c.basicPrice) +
    (withTarget ? parseFloat(c.targetPrice) : 0);
  const scaleToNest = (parentPct) =>
    (parseFloat(parentPct) / nestPct) * 100;
  const quantity = scaleToNest(c.quantity);
  const basicPrice = scaleToNest(c.basicPrice);
  const targetPrice = scaleToNest(c.targetPrice);
  const reserved = quantity + basicPrice + (withTarget ? targetPrice : 0);

  const laneKeys = [
    "sNo",
    "sourcePincode",
    "destinationPincode",
    "sourceCity",
    "destinationCity",
    "truckName",
    "truckBody",
    "loadability",
    "lengthFt",
    "widthFt",
    "heightFt",
  ];
  const base = MATERIAL_TABLE_COLUMN_WIDTHS.boq;
  const laneSum = laneKeys.reduce((sum, key) => sum + parseFloat(base[key]), 0);
  const laneBudget = Math.max(100 - reserved, 1);
  const widths = {
    quantity: `${quantity.toFixed(2)}%`,
    basicPrice: `${basicPrice.toFixed(2)}%`,
    targetPrice: `${targetPrice.toFixed(2)}%`,
  };
  laneKeys.forEach((key) => {
    widths[key] = `${((parseFloat(base[key]) / laneSum) * laneBudget).toFixed(2)}%`;
  });
  return widths;
}

function getBoqLineAmount(row, boqIndex, boqItem) {
  const unitPrice = parseDecimalValue(row?.boqPrices?.[boqIndex]?.basicPrice);
  const qty = getBoqItemQuantity(row, boqIndex, boqItem);
  return {
    unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
    qty,
    lineBasic: Number.isFinite(unitPrice) ? unitPrice * qty : 0,
  };
}

/** Last submitted materialQuotes (live quotes, else latest quoteRevision). */
function getLastQuotedMaterialQuotes(lrfq) {
  const live = Array.isArray(lrfq?.materialQuotes) ? lrfq.materialQuotes : [];
  if (live.length > 0) return live;
  const revs = Array.isArray(lrfq?.quoteRevisions)
    ? [...lrfq.quoteRevisions]
    : [];
  revs.sort((a, b) => {
    const ra = Number(a?.revisionNo);
    const rb = Number(b?.revisionNo);
    if (Number.isFinite(ra) && Number.isFinite(rb) && ra !== rb) return ra - rb;
    return (
      new Date(a?.createdAt || a?.updatedAt || 0).getTime() -
      new Date(b?.createdAt || b?.updatedAt || 0).getTime()
    );
  });
  for (let i = revs.length - 1; i >= 0; i -= 1) {
    const quotes = revs[i]?.materialQuotes;
    if (Array.isArray(quotes) && quotes.length > 0) return quotes;
  }
  return [];
}

function findQuotedMaterial(lrfq, material, matIndex) {
  const quotes = getLastQuotedMaterialQuotes(lrfq);
  const uid = String(material?.uniqueId || "").trim();
  if (uid) {
    const byId = quotes.find(
      (q) => String(q?.uniqueId || "").trim() === uid,
    );
    if (byId) return byId;
  }
  return quotes[matIndex] || null;
}

function findQuotedBoqLane(quotedMaterial, boqItem, boqIndex) {
  const lanes = Array.isArray(quotedMaterial?.boq)
    ? quotedMaterial.boq
    : Array.isArray(quotedMaterial?.boqItems)
      ? quotedMaterial.boqItems
      : [];
  const uid = String(boqItem?.uniqueId || "").trim();
  if (uid) {
    const byId = lanes.find((l) => String(l?.uniqueId || "").trim() === uid);
    if (byId) return byId;
  }
  return lanes[boqIndex] || null;
}

/** Last submitted terms (with Quote Response), else root terms. */
function getLastQuotedTerms(lrfq) {
  const revs = Array.isArray(lrfq?.quoteRevisions)
    ? [...lrfq.quoteRevisions]
    : [];
  revs.sort((a, b) => {
    const ra = Number(a?.revisionNo);
    const rb = Number(b?.revisionNo);
    if (Number.isFinite(ra) && Number.isFinite(rb) && ra !== rb) return ra - rb;
    return (
      new Date(a?.createdAt || a?.updatedAt || 0).getTime() -
      new Date(b?.createdAt || b?.updatedAt || 0).getTime()
    );
  });
  for (let i = revs.length - 1; i >= 0; i -= 1) {
    const terms = revs[i]?.terms;
    if (Array.isArray(terms) && terms.length > 0) return terms;
  }
  if (Array.isArray(lrfq?.supplierTerms) && lrfq.supplierTerms.length > 0) {
    return lrfq.supplierTerms;
  }
  return Array.isArray(lrfq?.terms) ? lrfq.terms : [];
}

/**
 * Flat per-material price rows for the materials table.
 * BOQ parents keep one boqPrices entry per child row.
 * Prefers last quoted materialQuotes for prices + technicalResponse.
 */
function buildInitialMatPrices(materials, lrfq) {
  return materials.map((mat, matIdx) => {
    const quoted = findQuotedMaterial(lrfq, mat, matIdx);
    const boqItems = getMaterialBoqItems(mat);
    return {
      matIdx,
      uniqueId: mat.uniqueId || "",
      basicPrice: toEditableNumberInput(
        quoted?.basicPrice ?? mat.basicPrice,
      ),
      technicalResponse: normalizeQuoteResponse(
        quoted?.technicalResponse ||
          quoted?.comments ||
          mat.technicalResponse ||
          mat.comments ||
          "",
      ),
      boqPrices: boqItems.map((boqItem, boqIndex) => {
        const quotedLane = findQuotedBoqLane(quoted, boqItem, boqIndex);
        return {
          basicPrice: toEditableNumberInput(
            quotedLane?.basicPrice ?? boqItem.basicPrice,
          ),
          quantity:
            toEditableNumberInput(
              quotedLane?.quantity ?? boqItem.quantity,
            ) || "1",
        };
      }),
    };
  });
}

function calcMatAmounts(row, material) {
  const boqItems = getMaterialBoqItems(material);
  const hasBoq = boqItems.length > 0;
  // Parent Basic Price: BOQ = Σ(lane unit × lane qty); non-BOQ = entered unit.
  const basicPrice = hasBoq
    ? boqItems.reduce(
        (sum, boqItem, boqIndex) =>
          sum + getBoqLineAmount(row, boqIndex, boqItem).lineBasic,
        0,
      )
    : parseDecimalValue(row?.basicPrice);
  const qty = Number(material?.quantity ?? row?.quantity) || 1;
  const safeBasic = Number.isFinite(basicPrice) ? basicPrice : 0;
  // Total Value = parent Basic Price × parent Qty (e.g. 1250 × 10 = 12500).
  const totalValue = safeBasic * qty;
  return {
    basicPrice: safeBasic,
    qty,
    // lineBasic stays as parent basic (no qty) for Gross Total basic column.
    lineBasic: safeBasic,
    totalValue,
    hasBoq,
    boqItems,
  };
}

function buildInitialSupplierTerms(lrfq) {
  const ownerTerms = Array.isArray(lrfq?.terms) ? lrfq.terms : [];
  const quotedTerms = getLastQuotedTerms(lrfq);
  const quoteMap = new Map();
  quotedTerms.forEach((t) => {
    const key = String(t?.rfqTerm || "")
      .trim()
      .toLowerCase();
    if (!key) return;
    quoteMap.set(key, t);
  });

  const source = ownerTerms.length > 0 ? ownerTerms : quotedTerms;
  if (source.length === 0) return [];

  return source.map((t) => {
    const key = String(t?.rfqTerm || "")
      .trim()
      .toLowerCase();
    const existing = key ? quoteMap.get(key) : null;
    const fromRow = normalizeQuoteResponse(
      t.quoteResponse || t.comments || t.termsComments || "",
    );
    const fromQuoted = normalizeQuoteResponse(
      existing?.quoteResponse ||
        existing?.comments ||
        existing?.termsComments ||
        "",
    );
    return {
      sno: t.sno,
      rfqTerm: t.rfqTerm || "",
      termsResponse: t.termsResponse || existing?.termsResponse || "",
      // Prefer last quoted response; treat "-" as empty so it does not block.
      comments: fromQuoted || fromRow || "",
    };
  });
}

function emptySubmitErrors() {
  return { formMessage: "", terms: {}, boq: {}, attachmentMessage: "" };
}

function validateQuoteSubmission(
  matPriceRows,
  supplierTerms,
  materials,
  pendingFiles,
  visibleSavedAttachments,
  ownerHasAttachments,
  requiredPdfFiles,
) {
  const errors = emptySubmitErrors();
  let hasErrors = false;

  if (materials.length === 0) {
    errors.formMessage = "No materials available to submit.";
    return { hasErrors: true, errors };
  }

  // Mandatory attachment: if owner provided reference docs, supplier must attach
  if (ownerHasAttachments) {
    const total = visibleSavedAttachments.length + pendingFiles.length;
    if (total === 0) {
      errors.attachmentMessage =
        "Please attach your quotation document before submitting.";
      hasErrors = true;
    }
  }

  // Per-file mandatory check: supplier must upload a file whose name matches each required PDF
  if (Array.isArray(requiredPdfFiles) && requiredPdfFiles.length > 0) {
    const allUploadedNames = [
      ...visibleSavedAttachments.map((a) =>
        String(a.originalFileName || "").trim().toLowerCase(),
      ),
      ...pendingFiles.map((f) => String(f.name || "").trim().toLowerCase()),
    ];
    const missingFiles = requiredPdfFiles.filter((req) => {
      const required = String(req.originalFileName || "").trim().toLowerCase();
      return !allUploadedNames.includes(required);
    });
    if (missingFiles.length > 0) {
      const names = missingFiles
        .map((f) => `"${f.originalFileName}"`)
        .join(", ");
      errors.attachmentMessage =
        `Please upload the required Document${missingFiles.length > 1 ? "s" : ""}: ${names}`;
      hasErrors = true;
    }
  }

  // Per-material price validation — Basic Price required.
  // For BOQ parents the Basic Price is rolled up, so each child price is required.
  matPriceRows.forEach((row) => {
    const key = `mat-${row.matIdx}`;
    const material = materials[row.matIdx];
    const fieldErrors = {};
    const boqItems = getMaterialBoqItems(material);

    if (boqItems.length > 0) {
      const boqErrors = {};
      boqItems.forEach((boqItem, boqIndex) => {
        const boqPrice = row.boqPrices?.[boqIndex]?.basicPrice;
        const boqQuantity = row.boqPrices?.[boqIndex]?.quantity;
        if (!String(boqPrice || "").trim()) {
          boqErrors[boqIndex] = "Price is required.";
          hasErrors = true;
        } else if (!Number.isFinite(parseDecimalValue(boqPrice))) {
          boqErrors[boqIndex] = "Enter a valid price.";
          hasErrors = true;
        }

        if (!String(boqQuantity || "").trim()) {
          boqErrors[boqIndex] = boqErrors[boqIndex] || "Quantity is required.";
          hasErrors = true;
        } else {
          const quantity = parseDecimalValue(boqQuantity);
          if (!Number.isFinite(quantity) || quantity <= 0) {
            boqErrors[boqIndex] = boqErrors[boqIndex] || "Enter a quantity above zero.";
            hasErrors = true;
          }
        }
      });
      if (Object.keys(boqErrors).length > 0) {
        fieldErrors.boqPrices = boqErrors;
      }
    } else if (!String(row.basicPrice || "").trim()) {
      fieldErrors.basicPrice = "Price is required.";
      hasErrors = true;
    } else if (!Number.isFinite(parseDecimalValue(row.basicPrice))) {
      fieldErrors.basicPrice = "Enter a valid price.";
      hasErrors = true;
    }

    if (!String(row.technicalResponse || "").trim()) {
      fieldErrors.technicalResponse = "Technical Response is required.";
      hasErrors = true;
    }

    if (Object.keys(fieldErrors).length > 0) {
      errors.boq[key] = fieldErrors;
    }
  });

  // Terms Quote Response required
  supplierTerms.forEach((row, index) => {
    if (!String(row.comments || "").trim()) {
      errors.terms[index] = "Quote Response is required.";
      hasErrors = true;
    }
  });

  if (hasErrors && !errors.formMessage) {
    errors.formMessage =
      "Please complete all mandatory fields before submitting.";
  }

  return { hasErrors, errors };
}

// ─── Attachment helpers (identical to Rfq-Supplier-view) ─────────────────────

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || "",
    originalFileName: item?.originalFileName || "",
    contentType: item?.contentType || "",
    uploadedAt: item?.uploadedAt || "",
    url: item?.url || "",
  };
}

function getAttachmentLabel(att) {
  if (!att) return "Attachment";
  if (att.originalFileName) return att.originalFileName;
  const key = att.s3Key || "";
  if (!key) return "Attachment";
  const parts = String(key).split("/");
  return parts[parts.length - 1] || key;
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || "").trim();
  const dotExt = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (dotExt && dotExt.length <= 6) return dotExt;
  const typeMap = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/msword": "doc",
    "text/plain": "txt",
    "application/zip": "zip",
  };
  return typeMap[String(contentType || "").toLowerCase()] || "";
}

function isSupplierFileAllowed(file) {
  const ext = getFileExtension(file?.name, file?.type);
  return SUPPLIER_ALLOWED_EXTENSIONS.includes(ext);
}

function formatAllowedFormatsList() {
  return SUPPLIER_ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ");
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(
    extension || getFileExtension("", contentType),
  ).toLowerCase();
  if (ext === "pdf")
    return { icon: "fa-file-pdf", color: "#dc3545", bg: "#fde8ea" };
  if (ext === "xlsx" || ext === "xls" || ext === "csv")
    return { icon: "fa-file-excel", color: "#198754", bg: "#e8f5ee" };
  if (ext === "docx" || ext === "doc")
    return { icon: "fa-file-word", color: "#0d6efd", bg: "#e7f1ff" };
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext))
    return { icon: "fa-file-image", color: "#6f42c1", bg: "#f3ebff" };
  if (ext === "zip" || ext === "rar" || ext === "7z")
    return { icon: "fa-file-zipper", color: "#fd7e14", bg: "#fff3e6" };
  return { icon: "fa-file", color: "#495057", bg: "#f8f9fa" };
}

// ─── Sub-components (identical to Rfq-Supplier-view) ─────────────────────────

const AttachmentTileGrid = ({
  attachments,
  downloadingS3Key,
  onDownload,
  disabled,
  onRemove,
}) => {
  if (!attachments?.length) return null;

  return (
    <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att);
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType,
        );
        const isDownloading = downloadingS3Key === att.s3Key;
        return (
          <div
            key={att.s3Key || fileName}
            style={{ position: "relative", width: "92px" }}
          >
            <button
              type="button"
              className="border-0 text-center w-100"
              title={`Open ${fileName}`}
              onClick={() => onDownload(att.s3Key, att.url)}
              disabled={disabled || isDownloading}
              style={{
                padding: "10px 8px",
                borderRadius: "10px",
                backgroundColor: fileMeta.bg,
                border: `1px solid ${fileMeta.color}33`,
                cursor: disabled || isDownloading ? "not-allowed" : "pointer",
                opacity: isDownloading ? 0.7 : 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  color: fileMeta.color,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  marginBottom: "8px",
                }}
              >
                {isDownloading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <i className={`fa-solid ${fileMeta.icon}`} />
                )}
              </div>
              <div
                title={fileName}
                style={{
                  fontSize: "11px",
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  color: "#212529",
                  width: "100%",
                }}
              >
                {fileName.length > 22
                  ? `${fileName.slice(0, 19)}...`
                  : fileName}
              </div>
            </button>
            {onRemove ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                title="Remove"
                onClick={() => onRemove(att.s3Key)}
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  padding: "0 5px",
                  lineHeight: "18px",
                  borderRadius: "50%",
                }}
              >
                ×
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title, actions = null }) => (
  <div className="row mb-2 mt-3 align-items-center">
    <div className={actions ? "col-12 col-md" : "col-12"}>
      <h5 className="text-primary border-bottom pb-2 mb-3 mb-md-0">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
    {actions ? (
      <div className="col-12 col-md-auto mb-3 mb-md-0 text-md-right">
        {actions}
      </div>
    ) : null}
  </div>
);

// ─── PublicPageShell — same as RfqSuppliers.js ───────────────────────────────

const PublicPageShell = ({ children }) => (
  <div className="wrapper" style={{ minHeight: "100vh" }}>
    <nav
      className="main-header navbar navbar-expand navbar-white navbar-light border-bottom"
      style={{ marginLeft: 0 }}
    >
      <div className="container-fluid">
        <span className="navbar-brand mb-0 h1 d-flex align-items-center">
          <img
            src="/assets/img/favicon/InstaTenders - Logo reduced.png"
            alt="Logo"
            style={{ height: 36 }}
          />
        </span>
      </div>
    </nav>
    <div className="content-wrapper" style={{ marginLeft: 0 }}>
      {children}
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const LrfqSupplierView = () => {
  const {
    id,
    supplierMailId: supplierMailIdParam,
    supplierId: supplierIdParam,
  } = useParams();
  const termsSectionRef = useRef(null);
  const supplierAttachmentInputRef = useRef(null);
  const quoteImportInputRef = useRef(null);

  const [lrfq, setLrfq] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [downloadingS3Key, setDownloadingS3Key] = useState("");
  const [isPriceEditMode, setIsPriceEditMode] = useState(false);
  const [matPriceRows, setMatPriceRows] = useState([]);
  const [supplierTerms, setSupplierTerms] = useState([]);
  const [submitErrors, setSubmitErrors] = useState(emptySubmitErrors());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExportingQuote, setIsExportingQuote] = useState(false);
  const [isImportingQuote, setIsImportingQuote] = useState(false);
  const [materialsPage, setMaterialsPage] = useState(0);
  const [boqLanePageByMaterial, setBoqLanePageByMaterial] = useState({});
  const [pendingSupplierFiles, setPendingSupplierFiles] = useState([]);
  const [removedSupplierAttachmentKeys, setRemovedSupplierAttachmentKeys] =
    useState(() => new Set());
  const [isUploadingSupplierAttachments, setIsUploadingSupplierAttachments] =
    useState(false);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

  const supplierMailId = useMemo(() => {
    if (!supplierMailIdParam) return "";
    try {
      return decodeURIComponent(supplierMailIdParam).trim();
    } catch {
      return String(supplierMailIdParam).trim();
    }
  }, [supplierMailIdParam]);
  const supplierId = supplierIdParam || localStorage.getItem("supplierId") || "";
  const currentUserId = localStorage.getItem("id") || "";

  // ── Derived ───────────────────────────────────────────────────────────────

  const materials = useMemo(
    () => (Array.isArray(lrfq?.materials) ? lrfq.materials : []),
    [lrfq],
  );

  // lrfqOwner = owner reference attachments (lrfq uses lrfqOwner)
  const lrfqOwnerAttachments = useMemo(() => {
    if (!Array.isArray(lrfq?.lrfqOwner)) return [];
    return lrfq.lrfqOwner
      .map(mapAttachmentFromApi)
      .filter((att) => att.s3Key);
  }, [lrfq?.lrfqOwner]);

  // If owner shared reference docs OR explicitly required PDF upload for specific files → supplier MUST attach at least one file
  const ownerHasAttachments = lrfqOwnerAttachments.length > 0;
  const requiresPdfUploadKeys = useMemo(() => {
    const keys = lrfq?.requiresPdfUploadKeys;
    return Array.isArray(keys) ? keys : [];
  }, [lrfq?.requiresPdfUploadKeys]);
  const requiresPdfUpload = Boolean(lrfq?.requiresPdfUpload) || requiresPdfUploadKeys.length > 0;
  const mustAttachFile = ownerHasAttachments || requiresPdfUpload;

  // Files that the supplier MUST upload (signed versions) — cross-reference keys → filenames
  const requiredPdfFiles = useMemo(() => {
    if (requiresPdfUploadKeys.length === 0) return [];
    return requiresPdfUploadKeys
      .map((key) => {
        const ownerFile = lrfqOwnerAttachments.find((a) => a.s3Key === key);
        return ownerFile
          ? { s3Key: key, originalFileName: ownerFile.originalFileName || key }
          : { s3Key: key, originalFileName: key };
      });
  }, [requiresPdfUploadKeys, lrfqOwnerAttachments]);

  const savedSupplierAttachments = useMemo(() => {
    if (!Array.isArray(lrfq?.attachments)) return [];
    return lrfq.attachments
      .map(mapAttachmentFromApi)
      .filter((att) => att.s3Key);
  }, [lrfq?.attachments]);

  const visibleSupplierAttachments = useMemo(
    () =>
      savedSupplierAttachments.filter(
        (att) => !removedSupplierAttachmentKeys.has(att.s3Key),
      ),
    [savedSupplierAttachments, removedSupplierAttachmentKeys],
  );

  const supplierAttachmentCount =
    visibleSupplierAttachments.length + pendingSupplierFiles.length;

  const quoteSubmitted = isQuoteSubmitted(lrfq);
  const isQuotationClosed = !canSupplierUpdateLrfqQuote(lrfq);
  const targetPriceDueDate = getSupplierPortalTargetPriceDueDate(lrfq);
  const mainDuePassed = isRfqDueDatePassed(lrfq?.lrfqDueDate);
  const canUpdateQuote = !isQuotationClosed;
  // Keep the full Materials table (including price columns) visible at all times.
  const showMaterialPricing = materials.length > 0;
  const showTermsComments =
    quoteSubmitted ||
    (isPriceEditMode && canUpdateQuote) ||
    supplierTerms.some((t) => String(t.comments || "").trim());

  const lrfqTargetMap = useMemo(() => buildLrfqTargetMap(lrfq), [lrfq]);
  const previousQuotePriceMap = useMemo(
    () => buildPreviousQuotePriceMap(lrfq),
    [lrfq],
  );
  const previousQuoteMaxTotal = useMemo(
    () => getPreviousQuoteMaxTotal(lrfq),
    [lrfq],
  );

  // First quote: ignore targets. After submit + targets exist → price matching.
  const isPriceMatchingMode =
    quoteSubmitted && hasLrfqTargetPrice(lrfq, lrfqTargetMap);

  // Show Target Price column whenever price-matching targets exist (not only when quote ≥ target).
  const showPriceMatchTargetColumn = useMemo(() => {
    if (!isPriceMatchingMode) return false;
    return materials.some((material) => {
      const boqItems = getMaterialBoqItems(material);
      if (boqItems.length > 0) {
        return boqItems.some((boqItem) =>
          hasLineTargetPrice(getLineTargetPrice(boqItem, lrfqTargetMap)),
        );
      }
      return hasLineTargetPrice(getLineTargetPrice(material, lrfqTargetMap));
    });
  }, [isPriceMatchingMode, materials, lrfqTargetMap]);

  // Source Code → Basic Price so nested Basic Price aligns under parent Basic Price.
  const nestedBoqColSpan = showPriceMatchTargetColumn ? 7 : 6;
  const boqColWidths = getBoqColumnWidths(showPriceMatchTargetColumn);

  // ── Gross totals ──────────────────────────────────────────────────────────

  const grossTotals = useMemo(() => {
    const calculated = matPriceRows.reduce(
      (acc, row) => {
        const mat = materials[row.matIdx];
        const qty = Number(mat?.quantity) || 1;
        const rowWithQty = { ...row, quantity: qty };
        const { basicPrice, totalValue } = calcMatAmounts(rowWithQty, mat);
        return {
          // Gross basic = Σ parent Basic Price (no parent qty).
          grossBasicPrice: acc.grossBasicPrice + basicPrice,
          // Gross total = Σ (parent Basic × parent Qty).
          grossTotalValue: acc.grossTotalValue + totalValue,
        };
      },
      { grossBasicPrice: 0, grossTotalValue: 0 },
    );

    if (!quoteSubmitted || isPriceEditMode) return calculated;

    const gross =
      lrfq?.grossTotal && typeof lrfq.grossTotal === "object"
        ? lrfq.grossTotal
        : {};
    return {
      grossBasicPrice:
        Number(gross.grossTotalBasicPrice) || calculated.grossBasicPrice,
      grossTotalValue:
        Number(gross.grossTotalValue) || calculated.grossTotalValue,
    };
  }, [matPriceRows, materials, quoteSubmitted, isPriceEditMode, lrfq]);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchDetail = useCallback(async () => {
    if (!id) {
      setLoadError("LRFQ ID is missing from the link.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError("");
    try {
      const response = await axios.post(API_URL, {
        action: "get-supplier-lrfq-detail",
        lrfqid: id,
        supplierId: supplierId || undefined,
        supplierMailId: supplierMailId || undefined,
        email: supplierMailId || undefined,
        currentUserId: currentUserId || undefined,
      });

      const responseData = normalizeApiPayload(
        parseApiResponse(response.data),
      );

      if (isApiSuccess(responseData)) {
        const row = pickLrfqFromResponse(responseData);
        if (row) {
          setLrfq(row);
        } else {
          setLrfq(null);
          setLoadError("LRFQ not found.");
          toast.error("LRFQ not found", { autoClose: 2000 });
        }
      } else {
        const message =
          responseData?.message || "Failed to fetch LRFQ details";
        setLrfq(null);
        setLoadError(message);
        toast.error(message, { autoClose: 2000 });
      }
    } catch (error) {
      console.error("Fetch supplier LRFQ detail error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const message =
        errData?.message ||
        "An error occurred while fetching LRFQ details";
      setLrfq(null);
      setLoadError(message);
      toast.error(message, { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  }, [id, supplierId, supplierMailId, currentUserId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    setMatPriceRows(buildInitialMatPrices(materials, lrfq));
  }, [materials, lrfq]);

  useEffect(() => {
    setMaterialsPage(0);
    setBoqLanePageByMaterial({});
  }, [lrfq?.id, materials.length]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(materials.length / MATERIALS_PAGE_SIZE) - 1,
    );
    setMaterialsPage((p) => Math.min(p, maxPage));
  }, [materials.length]);

  useEffect(() => {
    setSupplierTerms(buildInitialSupplierTerms(lrfq));
  }, [lrfq]);

  useEffect(() => {
    if (isQuotationClosed && isPriceEditMode) {
      setIsPriceEditMode(false);
    }
  }, [isQuotationClosed, isPriceEditMode]);

  // ── Downloads ─────────────────────────────────────────────────────────────

  const downloadRfqOwnerAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return;
    setDownloadingS3Key(s3Key);
    try {
      const response = await axios.post(API_URL, {
        action: "get-lrfq-owner-attachment-download-presigned-url",
        s3Keys: [s3Key],
        expiresIn: 3600,
      });
      const responseData = normalizeApiPayload(
        parseApiResponse(response.data),
      );
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to get download URL",
          { autoClose: 2000 },
        );
        return;
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl ||
        responseData.downloadUrl ||
        fallbackUrl;
      if (!downloadUrl) {
        toast.error("Download URL not available", { autoClose: 2000 });
        return;
      }
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Download attachment error:", error);
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Failed to download attachment", { autoClose: 2000 });
      }
    } finally {
      setDownloadingS3Key("");
    }
  };

  const downloadSupplierAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return;
    if (!supplierId) {
      toast.error("Supplier ID is missing. Please login again.", {
        autoClose: 2500,
      });
      return;
    }
    setDownloadingS3Key(s3Key);
    try {
      const response = await axios.post(API_URL, {
        action: "get-lrfq-supplier-attachment-download-presigned-url",
        lrfqNumber: lrfq?.lrfqNumber || lrfq?.LrfqNumber || "",
        lrfqid: id,
        supplierId,
        s3Keys: [s3Key],
        expiresIn: 3600,
      });
      const responseData = normalizeApiPayload(
        parseApiResponse(response.data),
      );
      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || "Failed to get download URL",
          { autoClose: 2000 },
        );
        return;
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl ||
        responseData.downloadUrl ||
        fallbackUrl;
      if (!downloadUrl) {
        toast.error("Download URL not available", { autoClose: 2000 });
        return;
      }
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Download supplier attachment error:", error);
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Failed to download attachment", { autoClose: 2000 });
      }
    } finally {
      setDownloadingS3Key("");
    }
  };

  // ── File attachment handlers ──────────────────────────────────────────────

  const handleSupplierAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const invalidFiles = files.filter((file) => !isSupplierFileAllowed(file));
    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type. Allowed formats: ${formatAllowedFormatsList()}`,
        { autoClose: 3000 },
      );
      return;
    }

    if (supplierAttachmentCount + files.length > MAX_SUPPLIER_ATTACHMENTS) {
      toast.error(
        `Maximum ${MAX_SUPPLIER_ATTACHMENTS} attachments allowed`,
        { autoClose: 2000 },
      );
      return;
    }

    setPendingSupplierFiles((prev) => [...prev, ...files]);
    // clear attachment error once a file is added
    setSubmitErrors((prev) => ({
      ...prev,
      attachmentMessage: "",
      formMessage: "",
    }));
  };

  const removePendingSupplierFile = (index) => {
    setPendingSupplierFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const markSupplierAttachmentForRemoval = (s3Key) => {
    setRemovedSupplierAttachmentKeys((prev) => {
      const next = new Set(prev);
      next.add(s3Key);
      return next;
    });
  };

  const uploadPendingSupplierAttachments = async () => {
    if (pendingSupplierFiles.length === 0) return [];

    setIsUploadingSupplierAttachments(true);
    try {
      const files = pendingSupplierFiles;
      const contentTypesForPresign = files.map((f) =>
        resolveFileContentType(f),
      );

      const response = await axios.post(API_URL, {
        action: "get-lrfq-supplier-attachment-upload-presigned-url",
        lrfqid: id,
        supplierId,
        fileNames: files.map((f) => f.name),
        contentTypes: contentTypesForPresign,
        expiresIn: 7200,
      });
      const responseData = normalizeApiPayload(
        parseApiResponse(response.data),
      );

      if (
        !isApiSuccess(responseData) ||
        !Array.isArray(responseData.files)
      ) {
        const invalidMsg = Array.isArray(responseData?.invalidFiles)
          ? `${responseData.message || "Invalid file type"}`
          : responseData?.message || "Failed to get upload URLs";
        toast.error(invalidMsg, { autoClose: 3000 });
        throw new Error(invalidMsg);
      }

      const presignedFiles = responseData.files;
      if (presignedFiles.length !== files.length) {
        throw new Error("Upload URL count mismatch");
      }

      for (let i = 0; i < files.length; i++) {
        const meta = presignedFiles[i];
        const file = files[i];
        const contentType =
          meta.contentType ||
          contentTypesForPresign[i] ||
          "application/octet-stream";

        if (!meta.uploadUrl) {
          throw new Error(`Missing uploadUrl for ${file.name}`);
        }
        if (!(file instanceof Blob) || file.size <= 0) {
          throw new Error(`Invalid file for upload: ${file.name}`);
        }

        const putRes = await fetch(meta.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: file,
        });

        if (!putRes.ok) {
          throw new Error(
            `S3 upload failed for ${file.name}: ${putRes.status}`,
          );
        }
      }

      return presignedFiles.map((meta, i) => ({
        s3Key: meta.s3Key,
        originalFileName:
          meta.originalFileName || meta.fileName || files[i].name,
        contentType:
          meta.contentType ||
          contentTypesForPresign[i] ||
          "application/octet-stream",
      }));
    } finally {
      setIsUploadingSupplierAttachments(false);
    }
  };

  // ── Material field change (flat material-level pricing) ──────────────────

  const clearMatFieldError = (matIdx, field) => {
    setSubmitErrors((prev) => {
      const key = `mat-${matIdx}`;
      const rowErrors = prev.boq[key];
      if (!rowErrors?.[field]) return prev;
      const nextRowErrors = { ...rowErrors };
      delete nextRowErrors[field];
      const nextBoq = { ...prev.boq };
      if (Object.keys(nextRowErrors).length === 0) {
        delete nextBoq[key];
      } else {
        nextBoq[key] = nextRowErrors;
      }
      return { ...prev, boq: nextBoq, formMessage: "" };
    });
  };

  const setMatFieldError = (matIdx, field, message) => {
    setSubmitErrors((prev) => {
      const key = `mat-${matIdx}`;
      const rowErrors = { ...(prev.boq[key] || {}) };
      if (message) rowErrors[field] = message;
      else delete rowErrors[field];
      const nextBoq = { ...prev.boq };
      if (Object.keys(rowErrors).length === 0) delete nextBoq[key];
      else nextBoq[key] = rowErrors;
      return { ...prev, boq: nextBoq, formMessage: "" };
    });
  };

  const handleMatChange = (matIdx, field, value) => {
    let nextValue = value;
    if (field !== "technicalResponse") {
      if (!isDecimalInput(value)) return;
      nextValue = normalizeDecimalInput(value);
      if (!isDecimalInput(nextValue)) return;
    }

    if (field === "basicPrice" && isPriceMatchingMode) {
      const mat = materials[matIdx];
      const uid = String(mat?.uniqueId || "").trim();
      const prevPrice = uid ? previousQuotePriceMap[uid] : NaN;
      const nextNum = parseDecimalValue(nextValue);
      if (
        Number.isFinite(prevPrice) &&
        Number.isFinite(nextNum) &&
        roundMoney(nextNum) > roundMoney(prevPrice)
      ) {
        setMatFieldError(
          matIdx,
          "basicPrice",
          `Cannot exceed previous quote (${formatAmount(prevPrice)})`,
        );
      } else {
        clearMatFieldError(matIdx, field);
      }
    } else {
      clearMatFieldError(matIdx, field);
    }

    setMatPriceRows((prev) =>
      prev.map((row) =>
        row.matIdx === matIdx ? { ...row, [field]: nextValue } : row,
      ),
    );
  };

  // ── BOQ child price and quantity changes ──────────────────────────────────

  const clearBoqFieldError = (matIdx, boqIndex) => {
    setSubmitErrors((prev) => {
      const key = `mat-${matIdx}`;
      const rowErrors = prev.boq[key];
      const boqPriceErrors = rowErrors?.boqPrices;
      if (!boqPriceErrors?.[boqIndex]) return prev;

      const nextBoqPriceErrors = { ...boqPriceErrors };
      delete nextBoqPriceErrors[boqIndex];
      const nextRowErrors = { ...rowErrors };
      if (Object.keys(nextBoqPriceErrors).length === 0) {
        delete nextRowErrors.boqPrices;
      } else {
        nextRowErrors.boqPrices = nextBoqPriceErrors;
      }

      const nextBoq = { ...prev.boq };
      if (Object.keys(nextRowErrors).length === 0) {
        delete nextBoq[key];
      } else {
        nextBoq[key] = nextRowErrors;
      }
      return { ...prev, boq: nextBoq, formMessage: "" };
    });
  };

  const setBoqFieldError = (matIdx, boqIndex, message) => {
    setSubmitErrors((prev) => {
      const key = `mat-${matIdx}`;
      const rowErrors = { ...(prev.boq[key] || {}) };
      const boqPrices = { ...(rowErrors.boqPrices || {}) };
      if (message) boqPrices[boqIndex] = message;
      else delete boqPrices[boqIndex];
      if (Object.keys(boqPrices).length === 0) delete rowErrors.boqPrices;
      else rowErrors.boqPrices = boqPrices;
      const nextBoq = { ...prev.boq };
      if (Object.keys(rowErrors).length === 0) delete nextBoq[key];
      else nextBoq[key] = rowErrors;
      return { ...prev, boq: nextBoq, formMessage: "" };
    });
  };

  const handleBoqFieldChange = (matIdx, boqIndex, field, value) => {
    if (!isDecimalInput(value)) return;
    const nextValue = normalizeDecimalInput(value);
    if (!isDecimalInput(nextValue)) return;

    if (field === "basicPrice" && isPriceMatchingMode) {
      const boqItem = getMaterialBoqItems(materials[matIdx])[boqIndex];
      const uid = String(boqItem?.uniqueId || "").trim();
      const prevPrice = uid ? previousQuotePriceMap[uid] : NaN;
      const nextNum = parseDecimalValue(nextValue);
      if (
        Number.isFinite(prevPrice) &&
        Number.isFinite(nextNum) &&
        roundMoney(nextNum) > roundMoney(prevPrice)
      ) {
        setBoqFieldError(
          matIdx,
          boqIndex,
          `Cannot exceed previous quote (${formatAmount(prevPrice)})`,
        );
      } else {
        clearBoqFieldError(matIdx, boqIndex);
      }
    } else {
      clearBoqFieldError(matIdx, boqIndex);
    }

    setMatPriceRows((prev) =>
      prev.map((row) => {
        if (row.matIdx !== matIdx) return row;
        const boqPrices = Array.isArray(row.boqPrices)
          ? [...row.boqPrices]
          : [];
        boqPrices[boqIndex] = {
          ...(boqPrices[boqIndex] || {}),
          [field]: nextValue,
        };
        return { ...row, boqPrices };
      }),
    );
  };

  const handleTermsCommentChange = (index, value) => {
    setSubmitErrors((prev) => {
      if (!prev.terms[index]) return prev;
      const nextTerms = { ...prev.terms };
      delete nextTerms[index];
      return { ...prev, terms: nextTerms, formMessage: "" };
    });
    setSupplierTerms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], comments: value };
      return next;
    });
  };

  // ── Export / Import quote Excel ───────────────────────────────────────────

  const handleExportSupplierQuote = async () => {
    if (!id) {
      toast.error("LRFQ ID is missing", { autoClose: 2000 });
      return;
    }
    if (!supplierMailId && !supplierId) {
      toast.error("Supplier identity is missing", { autoClose: 2000 });
      return;
    }
    if (isExportingQuote || isImportingQuote) return;

    setIsExportingQuote(true);
    try {
      const payload = {
        action: "export-lrfq-supplier-quote",
        lrfqid: id,
        email: supplierMailId || undefined,
      };
      if (supplierId) payload.supplierId = supplierId;

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const base64 =
        extractExcelBase64(responseData) || extractExcelBase64(response.data);

      if (!base64) {
        throw new Error(responseData?.message || "Invalid export response");
      }

      const lrfqNoSafe = String(
        lrfq?.lrfqNumber || lrfq?.LrfqNumber || id,
      ).replace(/[^\w.-]+/g, "_");
      downloadXlsxFromBase64(base64, `LRFQ-Quote-${lrfqNoSafe}.xlsx`);
      toast.success(
        responseData?.message ||
          "Quote Excel exported. Yellow cells are editable — do not change LineKey.",
        { autoClose: 3000 },
      );
    } catch (error) {
      console.error("Export LRFQ supplier quote error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || error.message || "Failed to export quote Excel",
        { autoClose: 3000 },
      );
    } finally {
      setIsExportingQuote(false);
    }
  };

  const applyExcelRowsToForm = (materialRows, termRows) => {
    const rows = Array.isArray(materialRows) ? materialRows : [];
    const terms = Array.isArray(termRows) ? termRows : [];
    if (rows.length === 0 && terms.length === 0) {
      return { applied: 0, termsApplied: 0 };
    }

    const lineMap = buildExcelLineTargetMap(materials);
    let applied = 0;

    const nextPrices = (Array.isArray(matPriceRows) ? matPriceRows : []).map(
      (row) => ({
        ...row,
        boqPrices: Array.isArray(row?.boqPrices)
          ? row.boqPrices.map((bp) => ({ ...bp }))
          : [],
      }),
    );

    rows.forEach((row, rowIndex) => {
      const hasEditableValue =
        pickExcelField(row, EXCEL_FIELD_ALIASES.basicPrice) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.technicalResponse) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.lineKey) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.materialCode);
      if (!hasEditableValue) return;

      const target = resolveExcelRowTarget(row, lineMap, materials, rowIndex);
      if (!target || !nextPrices[target.index]) return;

      const basicPrice = pickExcelField(row, EXCEL_FIELD_ALIASES.basicPrice);
      const technicalResponse = pickExcelField(
        row,
        EXCEL_FIELD_ALIASES.technicalResponse,
      );

      if (target.type === "boq") {
        const boqPrices = Array.isArray(nextPrices[target.index].boqPrices)
          ? [...nextPrices[target.index].boqPrices]
          : [];
        while (boqPrices.length <= target.boqIndex) {
          boqPrices.push({ basicPrice: "", quantity: "1" });
        }
        if (basicPrice !== "") {
          boqPrices[target.boqIndex] = {
            ...boqPrices[target.boqIndex],
            basicPrice,
          };
          nextPrices[target.index] = {
            ...nextPrices[target.index],
            boqPrices,
          };
          applied += 1;
        }
        return;
      }

      const patch = { ...nextPrices[target.index] };
      let touched = false;
      if (basicPrice !== "") {
        patch.basicPrice = basicPrice;
        touched = true;
      }
      if (technicalResponse !== "") {
        patch.technicalResponse = normalizeQuoteResponse(technicalResponse);
        touched = true;
      }
      if (touched) {
        nextPrices[target.index] = patch;
        applied += 1;
      }
    });

    if (applied > 0) {
      setMatPriceRows(nextPrices);
    }

    let termsApplied = 0;
    if (terms.length > 0) {
      const nextTerms = (Array.isArray(supplierTerms) ? supplierTerms : []).map(
        (row) => ({ ...row }),
      );
      terms.forEach((row, index) => {
        const rfqTerm = pickExcelField(row, EXCEL_FIELD_ALIASES.rfqTerm);
        const quoteResponse = pickExcelField(
          row,
          EXCEL_FIELD_ALIASES.quoteResponse,
        );
        if (quoteResponse === "" && !rfqTerm) return;

        let targetIndex = -1;
        if (rfqTerm) {
          targetIndex = nextTerms.findIndex(
            (t) =>
              String(t.rfqTerm || t.lrfqTerm || "")
                .trim()
                .toLowerCase() === rfqTerm.toLowerCase(),
          );
        }
        if (targetIndex < 0 && nextTerms[index]) targetIndex = index;
        if (targetIndex < 0 || !nextTerms[targetIndex]) return;

        if (quoteResponse !== "") {
          nextTerms[targetIndex] = {
            ...nextTerms[targetIndex],
            comments: normalizeQuoteResponse(quoteResponse),
          };
          termsApplied += 1;
        }
      });
      if (termsApplied > 0) {
        setSupplierTerms(nextTerms);
      }
    }

    return { applied, termsApplied };
  };

  const handleImportSupplierQuoteFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;
    if (!canUpdateQuote) {
      toast.error(
        "LRFQ due date has passed. Quotation can no longer be updated.",
        { autoClose: 2500 },
      );
      return;
    }

    const lowerName = String(file.name || "").toLowerCase();
    if (!lowerName.endsWith(".xlsx") && !lowerName.endsWith(".xls")) {
      toast.error("Please upload an Excel file (.xlsx)", { autoClose: 2500 });
      return;
    }
    if (isExportingQuote || isImportingQuote) return;

    setIsImportingQuote(true);
    setSubmitErrors(emptySubmitErrors());
    try {
      const { materials: materialRows, terms: termRows } =
        await parseSupplierQuoteExcelFile(file);
      const { applied, termsApplied } = applyExcelRowsToForm(
        materialRows,
        termRows,
      );

      if (applied === 0 && termsApplied === 0) {
        toast.error(
          "No matching quote rows found in Excel. Keep LineKey unchanged from export.",
          { autoClose: 3500 },
        );
        return;
      }

      setIsPriceEditMode(true);
      setShowSubmitSuccess(false);
      toast.success(
        `Imported into the form (${applied} material line${
          applied === 1 ? "" : "s"
        }${
          termsApplied > 0
            ? `, ${termsApplied} term${termsApplied === 1 ? "" : "s"}`
            : ""
        }). Review values, then click Submit to save.`,
        { autoClose: 4000 },
      );
    } catch (error) {
      console.error("Import LRFQ supplier quote error:", error);
      toast.error(
        error.message || "Failed to read quote Excel in the browser",
        { autoClose: 3000 },
      );
    } finally {
      setIsImportingQuote(false);
    }
  };

  // ── Edit mode ─────────────────────────────────────────────────────────────

  const handleStartPriceUpdate = () => {
    if (!canSupplierUpdateLrfqQuote(lrfq)) {
      const dueMsg = targetPriceDueDate
        ? ` Price-matching window ended on ${formatDueDateTime(targetPriceDueDate)}.`
        : " No price-matching counter-offer window is open for your account.";
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: `LRFQ due date has passed.${dueMsg}`,
      });
      return;
    }
    setSubmitErrors(emptySubmitErrors());
    setPendingSupplierFiles([]);
    setRemovedSupplierAttachmentKeys(new Set());
    setShowSubmitSuccess(false);
    // Re-seed from last quoted prices / Quote Response / Technical Response.
    setMatPriceRows(buildInitialMatPrices(materials, lrfq));
    setSupplierTerms(buildInitialSupplierTerms(lrfq));
    setIsPriceEditMode(true);
    requestAnimationFrame(() => {
      termsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleCancelPriceUpdate = () => {
    setMatPriceRows(buildInitialMatPrices(materials, lrfq));
    setSupplierTerms(buildInitialSupplierTerms(lrfq));
    setSubmitErrors(emptySubmitErrors());
    setPendingSupplierFiles([]);
    setRemovedSupplierAttachmentKeys(new Set());
    setIsPriceEditMode(false);
  };

  const scrollToFirstInvalidField = () => {
    requestAnimationFrame(() => {
      const firstInvalid = document.querySelector(".is-invalid");
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof firstInvalid.focus === "function") {
          firstInvalid.focus({ preventScroll: true });
        }
      }
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmitPrices = async () => {
    if (!canSupplierUpdateLrfqQuote(lrfq)) {
      const dueMsg = targetPriceDueDate
        ? ` Price-matching window ended on ${formatDueDateTime(targetPriceDueDate)}.`
        : " No price-matching counter-offer window is open for your account.";
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: `LRFQ due date has passed.${dueMsg}`,
      });
      return;
    }

    if (!supplierId) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: "Supplier ID is missing. Please login again.",
      });
      return;
    }

    const { hasErrors, errors } = validateQuoteSubmission(
      matPriceRows,
      supplierTerms,
      materials,
      pendingSupplierFiles,
      visibleSupplierAttachments,
      mustAttachFile,
      requiredPdfFiles,
    );
    if (hasErrors) {
      setSubmitErrors(errors);
      scrollToFirstInvalidField();
      return;
    }

    // After first quote + targets: cannot quote above previous submitted prices.
    if (isPriceMatchingMode) {
      const currencyLabel = lrfq?.lrfqCurrency ? ` ${lrfq.lrfqCurrency}` : "";
      const nextErrors = emptySubmitErrors();
      let hasOverPrevious = false;

      materials.forEach((mat, matIdx) => {
        const priceRow = matPriceRows.find((r) => r.matIdx === matIdx) || {};
        const key = `mat-${matIdx}`;
        const boqItems = getMaterialBoqItems(mat);
        if (boqItems.length > 0) {
          const boqErrors = {};
          boqItems.forEach((boqItem, boqIndex) => {
            const uid = String(boqItem.uniqueId || "").trim();
            const prev = uid ? previousQuotePriceMap[uid] : NaN;
            const next = parseDecimalValue(
              priceRow?.boqPrices?.[boqIndex]?.basicPrice,
            );
            if (
              Number.isFinite(prev) &&
              Number.isFinite(next) &&
              roundMoney(next) > roundMoney(prev)
            ) {
              boqErrors[boqIndex] =
                `Cannot exceed previous quote (${formatAmount(prev)})`;
              hasOverPrevious = true;
            }
          });
          if (Object.keys(boqErrors).length > 0) {
            nextErrors.boq[key] = {
              ...(nextErrors.boq[key] || {}),
              boqPrices: boqErrors,
            };
          }
          return;
        }
        const uid = String(mat.uniqueId || "").trim();
        const prev = uid ? previousQuotePriceMap[uid] : NaN;
        const next = parseDecimalValue(priceRow?.basicPrice);
        if (
          Number.isFinite(prev) &&
          Number.isFinite(next) &&
          roundMoney(next) > roundMoney(prev)
        ) {
          nextErrors.boq[key] = {
            ...(nextErrors.boq[key] || {}),
            basicPrice: `Cannot exceed previous quote (${formatAmount(prev)})`,
          };
          hasOverPrevious = true;
        }
      });

      if (hasOverPrevious) {
        nextErrors.formMessage =
          "Some line prices exceed your previous quotation. Enter the same amount or lower.";
        setSubmitErrors(nextErrors);
        scrollToFirstInvalidField();
        return;
      }

      if (
        Number.isFinite(previousQuoteMaxTotal) &&
        previousQuoteMaxTotal > 0 &&
        roundMoney(grossTotals.grossTotalValue) >
          roundMoney(previousQuoteMaxTotal)
      ) {
        setSubmitErrors({
          ...emptySubmitErrors(),
          formMessage: `Total Value cannot exceed your previous quotation of ${formatAmount(
            previousQuoteMaxTotal,
          )}${currencyLabel}. Enter the same amount or lower.`,
        });
        return;
      }
    }

    setSubmitErrors(emptySubmitErrors());
    setIsSubmitting(true);
    try {
      let newlyUploadedAttachments = [];
      if (pendingSupplierFiles.length > 0) {
        newlyUploadedAttachments = await uploadPendingSupplierAttachments();
      }

      const finalAttachments = [
        ...visibleSupplierAttachments,
        ...newlyUploadedAttachments,
      ];

      // Rebuild materialQuotes with per-material pricing
      const materialQuotes = materials.map((mat, matIdx) => {
        const priceRow = matPriceRows.find((r) => r.matIdx === matIdx) || {};
        const qty = Number(mat.quantity) || 1;
        const rowWithQty = { ...priceRow, quantity: qty };
        const { basicPrice, lineBasic, totalValue, hasBoq, boqItems } =
          calcMatAmounts(rowWithQty, mat);

        const quoteRow = {
          uniqueId: mat.uniqueId || "",
          sourceCode: mat.sourceCode || "",
          sourceDescription: mat.sourceDescription || "",
          hsn: mat.hsn || "",
          uom: mat.uom || "",
          quantity: mat.quantity || 0,
          isBoq: hasBoq || mat.isBoq || false,
          // BOQ parent basic = Σ(child basic × child qty); flat keeps line total.
          basicPrice: hasBoq ? basicPrice : lineBasic,
          gstPercent: 0,
          gstValue: 0,
          totalValue,
          technicalResponse: String(priceRow.technicalResponse || "").trim(),
        };

        if (hasBoq) {
          quoteRow.boq = boqItems.map((boqItem, boqIndex) => {
            const child = getBoqLineAmount(priceRow, boqIndex, boqItem);
            const laneQty = Number(boqItem.quantity);
            return {
              uniqueId: boqItem.uniqueId || "",
              sNo: boqItem.sNo || `${boqIndex + 1}`,
              sourcePincode: boqItem.sourcePincode || "",
              destinationPincode: boqItem.destinationPincode || "",
              sourceCity: boqItem.sourceCity || "",
              destinationCity: boqItem.destinationCity || "",
              truckName: boqItem.truckName || "",
              truckBody: boqItem.truckBody || "",
              // Keep string labels like "9 mt-FTL" (Number() would become 0).
              truckLoadabilityMt:
                boqItem.truckLoadabilityMt == null ||
                boqItem.truckLoadabilityMt === ""
                  ? ""
                  : boqItem.truckLoadabilityMt,
              truckLengthFt: Number(boqItem.truckLengthFt) || 0,
              truckWidthFt: Number(boqItem.truckWidthFt) || 0,
              truckHeightFt: Number(boqItem.truckHeightFt) || 0,
              quantity:
                Number.isFinite(laneQty) && laneQty > 0 ? laneQty : child.qty,
              basicPrice: child.unitPrice,
              gstPercent: 0,
              gstValue: 0,
              totalValue: child.lineBasic,
            };
          });
        }

        return quoteRow;
      });

      const payload = {
        action: "update-lrfq-supplier-quote",
        lrfqid: id,
        supplierId,
        currentUserId: currentUserId || undefined,
        terms: supplierTerms.map((row) => ({
          rfqTerm: String(row.rfqTerm || "").trim(),
          termsResponse: String(row.termsResponse || "").trim(),
          comments: String(row.comments || "").trim(),
        })),
        materialQuotes,
        grossTotalBasicPrice: grossTotals.grossBasicPrice,
        grossGstValue: 0,
        grossTotalValue: grossTotals.grossTotalValue,
      };

      if (supplierMailId) {
        payload.supplierMailId = supplierMailId;
        payload.email = supplierMailId;
      }

      // Price-matching update: include active targetPriceId when available.
      const targetPriceId = resolveLrfqTargetPriceId(lrfq);
      if (isPriceMatchingMode && targetPriceId) {
        payload.targetPriceId = targetPriceId;
      }

      if (finalAttachments.length > 0) {
        payload.attachments = finalAttachments.map((att) => ({
          s3Key: att.s3Key,
          originalFileName: att.originalFileName,
          contentType: att.contentType,
        }));
      } else if (
        savedSupplierAttachments.length > 0 ||
        removedSupplierAttachmentKeys.size > 0
      ) {
        payload.attachments = [];
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(
        parseApiResponse(response.data),
      );

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData.message ||
            "Thank you for your response. Your quotation has been submitted successfully.",
          { autoClose: 3000 },
        );
        setShowSubmitSuccess(true);
        setIsPriceEditMode(false);
        setPendingSupplierFiles([]);
        setRemovedSupplierAttachmentKeys(new Set());
        await fetchDetail();
      } else {
        toast.error(
          responseData?.message || "Failed to submit quote",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Submit LRFQ quote error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message ||
          error.message ||
          "An error occurred while submitting quote",
        { autoClose: 3000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Header ────────────────────────────────────────────────────────────────

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-12">
            <h1 className="m-0 txt_lft">Request for Quotation</h1>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Loading / not-found ───────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PublicPageShell>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr_rfq">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicPageShell>
    );
  }

  if (!lrfq) {
    return (
      <PublicPageShell>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr_rfq">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>{loadError || "LRFQ not found"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicPageShell>
    );
  }

  const lrfqNo = lrfq.lrfqNumber || lrfq.LrfqNumber || "-";
  const currency = lrfq.lrfqCurrency || "";

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <PublicPageShell>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr_rfq">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">

                    {/* ── LRFQ Details — same ViewField pattern as RFQ ── */}
                    <SectionTitle icon="fa-file-lines" title="LRFQ Details" />

                    <ViewField label="LRFQ Number">{lrfqNo}</ViewField>
                    <ViewField label="LRFQ Date">
                      {formatDate(lrfq.lrfqDate)}
                    </ViewField>
                    <ViewField label="LRFQ Due Date">
                      {formatDueDateTime(lrfq.lrfqDueDate)}
                    </ViewField>
                    {targetPriceDueDate ? (
                      <ViewField label="Price Matching Due">
                        {formatDueDateTime(targetPriceDueDate)}
                        {mainDuePassed ? (
                          <span
                            className={`badge ml-2 ${
                              canUpdateQuote
                                ? "badge-success"
                                : "badge-secondary"
                            }`}
                            style={{ fontSize: "10px" }}
                          >
                            {canUpdateQuote ? "Open" : "Closed"}
                          </span>
                        ) : null}
                      </ViewField>
                    ) : null}
                    <ViewField label="Currency">
                      {lrfq.lrfqCurrency || "-"}
                    </ViewField>
                    <ViewField label="LRFQ Summary">
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {lrfq.lrfqSummary || "-"}
                      </span>
                    </ViewField>

                    {/* ── Documents ── */}
                    {(lrfq.pdfDownloadUrl || lrfq.downloadUrl || lrfq.pdfLink || lrfqOwnerAttachments.length > 0) && (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        {(lrfq.pdfDownloadUrl || lrfq.downloadUrl || lrfq.pdfLink) ? (
                          <ViewField label="PDF Link">
                            <a
                              href={lrfq.pdfDownloadUrl || lrfq.downloadUrl || lrfq.pdfLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View PDF
                            </a>
                          </ViewField>
                        ) : null}
                        {lrfqOwnerAttachments.length > 0 ? (
                          <ViewField label="LRFQ Owner Attachments">
                            <AttachmentTileGrid
                              attachments={lrfqOwnerAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadRfqOwnerAttachment}
                              disabled={Boolean(downloadingS3Key)}
                            />
                          </ViewField>
                        ) : null}
                      </>
                    )}

                    {/* ── Terms — identical to RFQ ── */}
                    <div ref={termsSectionRef}>
                      <SectionTitle icon="fa-list-check" title="Terms" />
                    </div>

                    {supplierTerms.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th style={{ fontSize: "12px", width: "48px" }}>
                                S.No
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  minWidth: "160px",
                                }}
                              >
                                LRFQ Term
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  minWidth: "200px",
                                }}
                              >
                                Terms Response
                              </th>
                              {(showTermsComments || isPriceEditMode) && (
                                <th
                                  style={{
                                    fontSize: "12px",
                                    minWidth: "200px",
                                  }}
                                >
                                  Quote Response
                                  {isPriceEditMode ? (
                                    <span className="text-warning"> *</span>
                                  ) : null}
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {supplierTerms.map((row, index) => (
                              <tr key={index}>
                                <td style={{ verticalAlign: "top" }}>
                                  {row.sno ?? index + 1}
                                </td>
                                <td
                                  style={{
                                    verticalAlign: "top",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.rfqTerm || "-"}
                                </td>
                                <td
                                  style={{
                                    verticalAlign: "top",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.termsResponse || "-"}
                                </td>
                                {(showTermsComments || isPriceEditMode) && (
                                  <td style={{ verticalAlign: "top" }}>
                                    {isPriceEditMode ? (
                                      <>
                                        <textarea
                                          className={`form-control form-control-sm${
                                            submitErrors.terms[index]
                                              ? " is-invalid"
                                              : ""
                                          }`}
                                          rows={2}
                                          placeholder="Enter comments..."
                                          value={row.comments}
                                          onChange={(e) =>
                                            handleTermsCommentChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {submitErrors.terms[index] ? (
                                          <div className="invalid-feedback d-block">
                                            {submitErrors.terms[index]}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : (
                                      <span
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          display: "block",
                                        }}
                                      >
                                        {row.comments || "-"}
                                      </span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* ── Materials — flat table ── */}
                    <SectionTitle
                      icon="fa-boxes-stacked"
                      title="Materials"
                      actions={
                        materials.length > 0 ? (
                          <div className="d-inline-flex flex-wrap align-items-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary mr-2 mb-1"
                              onClick={handleExportSupplierQuote}
                              disabled={
                                isExportingQuote ||
                                isImportingQuote ||
                                isSubmitting
                              }
                              title="Download quote Excel (yellow = editable, do not change LineKey)"
                            >
                              {isExportingQuote ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm mr-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-file-export mr-1" />
                                  Export
                                </>
                              )}
                            </button>
                            {canUpdateQuote ? (
                              <>
                                <input
                                  ref={quoteImportInputRef}
                                  type="file"
                                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                  style={{ display: "none" }}
                                  onChange={handleImportSupplierQuoteFile}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary mb-1"
                                  onClick={() =>
                                    quoteImportInputRef.current?.click()
                                  }
                                  disabled={
                                    isExportingQuote ||
                                    isImportingQuote ||
                                    isSubmitting
                                  }
                                  title="Import Excel into the form (not saved until Submit)"
                                >
                                  {isImportingQuote ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm mr-1"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      Importing...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa-solid fa-file-import mr-1" />
                                      Import
                                    </>
                                  )}
                                </button>
                              </>
                            ) : null}
                          </div>
                        ) : null
                      }
                    />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">
                        No materials
                      </div>
                    ) : (
                      <>
                      <TablePaginationBar
                        page={materialsPage}
                        pageSize={MATERIALS_PAGE_SIZE}
                        totalItems={materials.length}
                        onPageChange={setMaterialsPage}
                        itemLabel="materials"
                      />
                      <div className="table-responsive mb-3">
                        <table
                          className="table table-bordered table-sm mb-0"
                          style={{
                            width: "100%",
                            minWidth: "960px",
                            tableLayout: "fixed",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: MATERIAL_TABLE_COLUMN_WIDTHS.category.sNo,
                                }}
                              >
                                S.No
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width:
                                    MATERIAL_TABLE_COLUMN_WIDTHS.category.sourceCode,
                                }}
                              >
                               Cluster
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width:
                                    MATERIAL_TABLE_COLUMN_WIDTHS.category
                                      .sourceDescription,
                                }}
                              >
                                Cluster Description
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: MATERIAL_TABLE_COLUMN_WIDTHS.category.hsn,
                                }}
                              >
                                HSN
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: MATERIAL_TABLE_COLUMN_WIDTHS.category.uom,
                                }}
                              >
                                UOM
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width:
                                    MATERIAL_TABLE_COLUMN_WIDTHS.category.quantity,
                                }}
                              >
                                Qty
                              </th>
                              {showMaterialPricing ? (
                                <>
                                  {showPriceMatchTargetColumn ? (
                                    <th
                                      style={{
                                        fontSize: "12px",
                                        width:
                                          MATERIAL_TABLE_COLUMN_WIDTHS.category
                                            .targetPrice,
                                      }}
                                    >
                                      Target Price
                                    </th>
                                  ) : null}
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      color: "#000000",
                                      width:
                                        MATERIAL_TABLE_COLUMN_WIDTHS.category
                                          .basicPrice,
                                      backgroundColor: "#dee2e6",
                                    }}
                                  >
                                    Basic Price
                                    {isPriceEditMode ? (
                                      <span className="text-warning"> *</span>
                                    ) : null}
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      color: "#000000",
                                      width:
                                        MATERIAL_TABLE_COLUMN_WIDTHS.category
                                          .totalValue,
                                      backgroundColor: "#dee2e6",
                                    }}
                                  >
                                    Total Value
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      width:
                                        MATERIAL_TABLE_COLUMN_WIDTHS.category
                                          .technicalResponse,
                                    }}
                                  >
                                    Technical Response
                                    {isPriceEditMode ? (
                                      <span className="text-warning"> *</span>
                                    ) : null}
                                  </th>
                                </>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {materials
                              .slice(
                                materialsPage * MATERIALS_PAGE_SIZE,
                                materialsPage * MATERIALS_PAGE_SIZE +
                                  MATERIALS_PAGE_SIZE,
                              )
                              .map((mat, pageOffset) => {
                              const matIdx =
                                materialsPage * MATERIALS_PAGE_SIZE + pageOffset;
                              const priceRow =
                                matPriceRows.find(
                                  (r) => r.matIdx === matIdx,
                                ) || {};
                              const qty = Number(mat.quantity) || 1;
                              const rowWithQty = { ...priceRow, quantity: qty };
                              const { basicPrice, totalValue, hasBoq, boqItems } =
                                calcMatAmounts(rowWithQty, mat);
                              const errKey = `mat-${matIdx}`;
                              const matErr = submitErrors.boq[errKey] || {};
                              const parentSNo = mat.sno ?? matIdx + 1;
                              const boqLanePage =
                                Number(boqLanePageByMaterial[matIdx]) || 0;
                              const boqLanePageCount = Math.max(
                                1,
                                Math.ceil(boqItems.length / BOQ_LANES_PAGE_SIZE),
                              );
                              const safeBoqLanePage = Math.min(
                                boqLanePage,
                                boqLanePageCount - 1,
                              );
                              const boqLaneStart =
                                safeBoqLanePage * BOQ_LANES_PAGE_SIZE;
                              const visibleBoqItems = hasBoq
                                ? boqItems.slice(
                                    boqLaneStart,
                                    boqLaneStart + BOQ_LANES_PAGE_SIZE,
                                  )
                                : [];

                              return (
                                <React.Fragment
                                  key={mat.uniqueId || `mat-${matIdx}`}
                                >
                                  <tr>
                                    <td className="align-middle fwt_600">
                                      {parentSNo}
                                      {hasBoq ? (
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#ffc107",
                                            color: "#212529",
                                            fontSize: "9px",
                                            lineHeight: 1.2,
                                            padding: "2px 5px",
                                            borderRadius: "4px",
                                            marginLeft: "6px",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          BOQ
                                        </span>
                                      ) : null}
                                    </td>
                                    <td className="align-middle">
                                      {renderCenteredDash(mat.sourceCode)}
                                    </td>
                                    <td className="align-middle">
                                      {renderCenteredDash(mat.sourceDescription)}
                                    </td>
                                    <td className="align-middle">
                                      {renderCenteredDash(mat.hsn)}
                                    </td>
                                    <td className="align-middle">
                                      {renderCenteredDash(mat.uom)}
                                    </td>
                                    <td className="align-middle text-right">
                                      {renderCenteredDash(mat.quantity)}
                                    </td>
                                    {showMaterialPricing ? (
                                      <>
                                        {showPriceMatchTargetColumn ? (
                                          <td className="text-right align-middle">
                                            {hasBoq
                                              ? "—"
                                              : (() => {
                                                  const target =
                                                    getLineTargetPrice(
                                                      mat,
                                                      lrfqTargetMap,
                                                    );
                                                  return hasLineTargetPrice(
                                                    target,
                                                  )
                                                    ? formatAmount(target)
                                                    : "—";
                                                })()}
                                          </td>
                                        ) : null}
                                        {/* Basic Price */}
                                        <td
                                          className={
                                            isPriceEditMode
                                              ? undefined
                                              : "text-right align-middle"
                                          }
                                        >
                                          {isPriceEditMode ? (
                                            hasBoq ? (
                                              <div
                                                className="text-right fwt_600"
                                                style={{
                                                  paddingRight: "4px",
                                                }}
                                              >
                                                {formatAmount(basicPrice)}
                                              </div>
                                            ) : (
                                              <>
                                                <input
                                                  type="text"
                                                  className={`form-control form-control-sm${
                                                    matErr.basicPrice
                                                      ? " is-invalid"
                                                      : ""
                                                  }`}
                                                  inputMode="decimal"
                                                  placeholder="-"
                                                  value={
                                                    priceRow.basicPrice || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleMatChange(
                                                      matIdx,
                                                      "basicPrice",
                                                      e.target.value,
                                                    )
                                                  }
                                                  style={{
                                                    width: "100%",
                                                    maxWidth: "96px",
                                                    paddingLeft: "4px",
                                                    paddingRight: "4px",
                                                    textAlign: priceRow.basicPrice
                                                      ? "right"
                                                      : "center",
                                                  }}
                                                />
                                                {matErr.basicPrice ? (
                                                  <div className="invalid-feedback d-block">
                                                    {matErr.basicPrice}
                                                  </div>
                                                ) : null}
                                              </>
                                            )
                                          ) : (
                                            formatAmount(
                                              hasBoq
                                                ? basicPrice
                                                : parseDecimalValue(
                                                    priceRow.basicPrice,
                                                  ) || mat.basicPrice || 0,
                                            )
                                          )}
                                        </td>
                                        {/* Total Value */}
                                        <td className="text-right fwt_600 align-middle">
                                          {formatAmount(
                                            isPriceEditMode || hasBoq
                                              ? totalValue
                                              : (mat.totalValue ?? totalValue),
                                          )}
                                        </td>
                                        {/* Technical Response */}
                                        <td style={{ verticalAlign: "top" }}>
                                          {isPriceEditMode ? (
                                            <>
                                              <textarea
                                                className={`form-control form-control-sm${
                                                  matErr.technicalResponse
                                                    ? " is-invalid"
                                                    : ""
                                                }`}
                                                rows={2}
                                                placeholder="Enter technical response..."
                                                value={
                                                  priceRow.technicalResponse ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleMatChange(
                                                    matIdx,
                                                    "technicalResponse",
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                              {matErr.technicalResponse ? (
                                                <div className="invalid-feedback d-block">
                                                  {matErr.technicalResponse}
                                                </div>
                                              ) : null}
                                            </>
                                          ) : (
                                            <span
                                              style={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                display: "block",
                                              }}
                                            >
                                              {renderCenteredDash(
                                                normalizeQuoteResponse(
                                                  priceRow.technicalResponse ||
                                                    mat.technicalResponse ||
                                                    "",
                                                ),
                                              )}
                                            </span>
                                          )}
                                        </td>
                                      </>
                                    ) : null}
                                  </tr>

                                  {hasBoq ? (
                                    <tr style={{ backgroundColor: "#fff8e6" }}>
                                      <td
                                        style={{ backgroundColor: "#fff8e6" }}
                                      />
                                      <td
                                        colSpan={nestedBoqColSpan}
                                        style={{
                                          padding: 0,
                                          border: "none",
                                          backgroundColor: "#fff8e6",
                                        }}
                                      >
                                        {boqItems.length >
                                        BOQ_LANES_PAGE_SIZE ? (
                                          <div
                                            style={{
                                              padding: "6px 8px 2px",
                                              backgroundColor: "#fff8e6",
                                            }}
                                          >
                                            <TablePaginationBar
                                              page={safeBoqLanePage}
                                              pageSize={BOQ_LANES_PAGE_SIZE}
                                              totalItems={boqItems.length}
                                              onPageChange={(next) =>
                                                setBoqLanePageByMaterial(
                                                  (prev) => ({
                                                    ...prev,
                                                    [matIdx]: next,
                                                  }),
                                                )
                                              }
                                              itemLabel="lanes"
                                            />
                                          </div>
                                        ) : null}
                                        <table
                                          className="table table-bordered table-sm mb-0"
                                          style={{
                                            width: "100%",
                                            minWidth: "100%",
                                            tableLayout: "fixed",
                                            backgroundColor: "#fff8e6",
                                          }}
                                        >
                                          <thead>
                                            <tr
                                              style={{
                                                backgroundColor: "#003366",
                                                color: "#fff",
                                              }}
                                            >
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.sNo,
                                                  minWidth: "64px",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                S.No
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.sourcePincode,
                                                }}
                                              >
                                                Source Pincode
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width:
                                                    boqColWidths.destinationPincode,
                                                }}
                                              >
                                                Destination Pincode
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.sourceCity,
                                                }}
                                              >
                                                Source City
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width:
                                                    boqColWidths.destinationCity,
                                                }}
                                              >
                                                Destination City
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.truckName,
                                                }}
                                              >
                                                Truck Name
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.truckBody,
                                                }}
                                              >
                                                Truck Body
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.loadability,
                                                }}
                                              >
                                                Loadability (MT)
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.lengthFt,
                                                }}
                                              >
                                                Length (Ft)
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.widthFt,
                                                }}
                                              >
                                                Width (Ft)
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.heightFt,
                                                }}
                                              >
                                                Height (Ft)
                                              </th>
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  width: boqColWidths.quantity,
                                                  backgroundColor: "#003366",
                                                  color: "#fff",
                                                }}
                                              >
                                                Qty
                                              </th>
                                              {showPriceMatchTargetColumn ? (
                                                <th
                                                  style={{
                                                    fontSize: "10px",
                                                    width:
                                                      boqColWidths.targetPrice,
                                                  }}
                                                >
                                                  Target Price
                                                </th>
                                              ) : null}
                                              <th
                                                style={{
                                                  fontSize: "10px",
                                                  color: "#0f0f0f",
                                                  width: boqColWidths.basicPrice,
                                                  backgroundColor: "#dee2e6",
                                                }}
                                              >
                                                Basic Price
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {visibleBoqItems.map(
                                              (boqItem, visibleOffset) => {
                                                const boqIndex =
                                                  boqLaneStart + visibleOffset;
                                                const childError =
                                                  matErr.boqPrices?.[boqIndex];
                                                const childSNo =
                                                  boqItem.sNo ||
                                                  `${parentSNo}.${boqIndex + 1}`;
                                                const childBasicPrice =
                                                  priceRow.boqPrices?.[boqIndex]
                                                    ?.basicPrice;
                                                const childQuantity =
                                                  priceRow.boqPrices?.[boqIndex]
                                                    ?.quantity;
                                                const laneTarget =
                                                  getLineTargetPrice(
                                                    boqItem,
                                                    lrfqTargetMap,
                                                  );

                                                return (
                                                  <tr
                                                    key={`boq-${matIdx}-${boqIndex}`}
                                                  >
                                                    <td
                                                      className="align-middle fwt_600"
                                                      style={{
                                                        paddingLeft: "8px",
                                                        paddingRight: "4px",
                                                        color: "#000000",
                                                        minWidth: "64px",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      {childSNo}
                                                    </td>
                                                    <td
                                                      className="align-middle"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.sourcePincode,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.destinationPincode,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.sourceCity,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.destinationCity,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.truckName,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.truckBody,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle text-right"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.truckLoadabilityMt,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle text-right"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.truckLengthFt,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle text-right"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.truckWidthFt,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle text-right"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        boqItem.truckHeightFt,
                                                      )}
                                                    </td>
                                                    <td
                                                      className="align-middle text-right"
                                                      style={{ fontSize: "11px" }}
                                                    >
                                                      {renderCenteredDash(
                                                        childQuantity ||
                                                          boqItem.quantity ||
                                                          1,
                                                      )}
                                                    </td>
                                                    {showPriceMatchTargetColumn ? (
                                                      <td className="text-right align-middle">
                                                        {hasLineTargetPrice(
                                                          laneTarget,
                                                        )
                                                          ? formatAmount(
                                                              laneTarget,
                                                            )
                                                          : "—"}
                                                      </td>
                                                    ) : null}
                                                    <td style={{ padding: "2px" }}>
                                                      <input
                                                        type="text"
                                                        className={`form-control form-control-sm${
                                                          childError
                                                            ? " is-invalid"
                                                            : ""
                                                        }`}
                                                        inputMode="decimal"
                                                        placeholder="-"
                                                        value={
                                                          childBasicPrice || ""
                                                        }
                                                        readOnly={!isPriceEditMode}
                                                        onChange={(e) =>
                                                          handleBoqFieldChange(
                                                            matIdx,
                                                            boqIndex,
                                                            "basicPrice",
                                                            e.target.value,
                                                          )
                                                        }
                                                        style={{
                                                          minWidth: 0,
                                                          padding: "2px 4px",
                                                          fontSize: "11px",
                                                          backgroundColor: "#fff",
                                                          textAlign: childBasicPrice
                                                            ? "right"
                                                            : "center",
                                                        }}
                                                      />
                                                      {childError ? (
                                                        <div className="invalid-feedback d-block">
                                                          {childError}
                                                        </div>
                                                      ) : null}
                                                    </td>
                                                  </tr>
                                                );
                                              },
                                            )}
                                          </tbody>
                                        </table>
                                      </td>
                                      <td
                                        colSpan={2}
                                        style={{
                                          backgroundColor: "#fff8e6",
                                          border: "none",
                                        }}
                                      />
                                    </tr>
                                  ) : null}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                          {showMaterialPricing && materials.length > 0 ? (
                            <tfoot>
                              <tr style={{ backgroundColor: "#dee2e6" }}>
                                <td
                                  colSpan={showPriceMatchTargetColumn ? 7 : 6}
                                  className="text-right fwt_600 align-middle"
                                >
                                  Gross Total
                                  {currency ? ` (${currency})` : ""}
                                </td>
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossBasicPrice)}
                                </td>
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossTotalValue)}
                                </td>
                                <td />
                              </tr>
                            </tfoot>
                          ) : null}
                        </table>
                      </div>
                      <TablePaginationBar
                        page={materialsPage}
                        pageSize={MATERIALS_PAGE_SIZE}
                        totalItems={materials.length}
                        onPageChange={setMaterialsPage}
                        itemLabel="materials"
                      />
                      </>
                    )}

                    {/* {isPriceMatchingMode &&
                    showMaterialPricing &&
                    showPriceMatchTargetColumn ? (
                      <div
                        className="alert alert-info mt-2 mb-3"
                        role="alert"
                        style={{ fontSize: "13px" }}
                      >
                        <i className="fa-solid fa-bullseye mr-2" />
                        Price matching is active. Per-line targets above your
                        quote are listed under Target Price
                        {Number.isFinite(previousQuoteMaxTotal) ? (
                          <>
                            . Max allowed Total Value:{" "}
                            <strong>
                              {formatAmount(previousQuoteMaxTotal)}
                              {currency ? ` ${currency}` : ""}
                            </strong>{" "}
                            (your previous quotation). Current Total Value:{" "}
                            <strong>
                              {formatAmount(grossTotals.grossTotalValue)}
                              {currency ? ` ${currency}` : ""}
                            </strong>
                          </>
                        ) : null}
                        . You cannot quote above your previous line prices.
                      </div>
                    ) : null} */}

                    {/* ── Supplier Attachments — show saved always; upload only in edit mode ── */}
                    {(isPriceEditMode ||
                      visibleSupplierAttachments.length > 0) && (
                      <>
                        <SectionTitle
                          icon="fa-paperclip"
                          title="Your Quotation Attachments"
                        />
                        {visibleSupplierAttachments.length > 0 && (
                          <div className="mb-3">
                            <AttachmentTileGrid
                              attachments={visibleSupplierAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadSupplierAttachment}
                              disabled={Boolean(downloadingS3Key)}
                              onRemove={isPriceEditMode ? markSupplierAttachmentForRemoval : undefined}
                            />
                          </div>
                        )}
                        {isPriceEditMode ? (
                          <div className="mb-3">
                            <p
                              className="text-muted mb-2"
                              style={{ fontSize: "13px" }}
                            >
                              Upload your quotation reference documents (max{" "}
                              {MAX_SUPPLIER_ATTACHMENTS} files). Allowed formats: {formatAllowedFormatsList()}.
                              {mustAttachFile && requiredPdfFiles.length === 0 && (
                                <strong className="text-danger">
                                  {" "}At least 1 file is required.
                                </strong>
                              )}
                            </p>

                            {/* Per-file required PDF labels */}
                            {requiredPdfFiles.length > 0 && (
                              <div className="mb-3">
                                {requiredPdfFiles.map((req) => {
                                  const allUploadedNames = [
                                    ...visibleSupplierAttachments.map((a) =>
                                      String(a.originalFileName || "").trim().toLowerCase(),
                                    ),
                                    ...pendingSupplierFiles.map((f) =>
                                      String(f.name || "").trim().toLowerCase(),
                                    ),
                                  ];
                                  const isSatisfied = allUploadedNames.includes(
                                    String(req.originalFileName || "").trim().toLowerCase(),
                                  );
                                  return (
                                    <div
                                      key={req.s3Key}
                                      className="d-flex align-items-center mb-1"
                                      style={{
                                        fontSize: "12px",
                                        padding: "5px 10px",
                                        borderRadius: "4px",
                                        color: isSatisfied ? "#155724" : "#721c24",
                                      }}
                                    >
                                      <i
                                        className={`fa-solid ${isSatisfied ? "fa-circle-check" : "fa-circle-exclamation"} mr-2`}
                                      />
                                      {isSatisfied
                                        ? `"${req.originalFileName}" — uploaded ✓`
                                        : `Required: "${req.originalFileName}" must be duly seal & signed file to be uploaded`}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {submitErrors.attachmentMessage ? (
                              <div
                                className="alert alert-danger mb-2"
                                role="alert"
                              >
                                {submitErrors.attachmentMessage}
                              </div>
                            ) : null}
                            <input
                              ref={supplierAttachmentInputRef}
                              type="file"
                              multiple
                              accept=".jpeg,.jpg,.png,.pdf,.xlsx,.ppt"
                              style={{ display: "none" }}
                              onChange={handleSupplierAttachmentFileSelect}
                              disabled={
                                isSubmitting || isUploadingSupplierAttachments
                              }
                            />
                            <button
                              type="button"
                              className="btn btn-blue btn-sm mb-3"
                              onClick={() =>
                                supplierAttachmentInputRef.current?.click()
                              }
                              disabled={
                                isSubmitting ||
                                isUploadingSupplierAttachments ||
                                supplierAttachmentCount >=
                                  MAX_SUPPLIER_ATTACHMENTS
                              }
                            >
                              <i className="fa-solid fa-paperclip mr-1" />
                              Choose Files
                            </button>

                            {pendingSupplierFiles.length > 0 ? (
                              <ul className="list-group mb-3">
                                {pendingSupplierFiles.map((file, index) => (
                                  <li
                                    key={`${file.name}-${index}`}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                  >
                                    <span>
                                      <i className="fa-solid fa-file mr-2" />
                                      {file.name}
                                      <small className="text-muted ml-2">
                                        Ready to upload on submit
                                      </small>
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        removePendingSupplierFile(index)
                                      }
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    )}

                    {/* ── Success banner — identical to RFQ ── */}
                    {(quoteSubmitted || showSubmitSuccess) &&
                    !isPriceEditMode ? (
                      <div
                        className="alert alert-success mt-4 mb-0 text-center"
                        role="alert"
                      >
                        <i className="fa-solid fa-circle-check mr-2" />
                        Thank you for your response. Your quotation has been
                        submitted successfully.
                      </div>
                    ) : null}

                    {mainDuePassed && canUpdateQuote && targetPriceDueDate ? (
                      <div
                        className="alert alert-info mt-4 mb-0"
                        role="alert"
                        style={{ fontSize: "13px" }}
                      >
                        <i className="fa-solid fa-circle-info mr-2" />
                        Main LRFQ due date has passed. You may update your quote
                        for price matching until{" "}
                        {formatDueDateTime(targetPriceDueDate)}.
                      </div>
                    ) : null}

                    {/* ── Action buttons — identical to RFQ ── */}
                    {materials.length > 0 && canUpdateQuote ? (
                      <div className="mt-4 pt-3 border-top">
                        {submitErrors.formMessage ? (
                          <div
                            className="alert alert-danger mb-3"
                            role="alert"
                          >
                            {submitErrors.formMessage}
                          </div>
                        ) : null}
                        <div className="d-flex justify-content-end flex-wrap">
                          {!isPriceEditMode ? (
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={handleStartPriceUpdate}
                            >
                              <i className="fa-solid fa-pen-to-square mr-1" />
                              Update Price
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary mr-2"
                                onClick={handleCancelPriceUpdate}
                                disabled={
                                  isSubmitting || isUploadingSupplierAttachments
                                }
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-blue"
                                onClick={handleSubmitPrices}
                                disabled={
                                  isSubmitting || isUploadingSupplierAttachments
                                }
                              >
                                {isSubmitting ||
                                isUploadingSupplierAttachments ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm mr-1"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-solid fa-paper-plane mr-1" />
                                    Submit
                                  </>
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : isQuotationClosed ? (
                      <div
                        className="alert mt-4 mb-0"
                        role="alert"
                        style={{
                          color: "#856404",
                          backgroundColor: "#fff3cd",
                          borderColor: "#ffeeba",
                        }}
                      >
                        <i className="fa-solid fa-triangle-exclamation mr-2" />
                        {mainDuePassed && targetPriceDueDate
                          ? `LRFQ due date has passed. Price-matching window ended on ${formatDueDateTime(targetPriceDueDate)}.`
                          : mainDuePassed
                            ? "LRFQ due date has passed. No price-matching counter-offer window is open for your account."
                            : "Quotation can no longer be updated."}
                      </div>
                    ) : null}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicPageShell>
  );
};

export default LrfqSupplierView;
